import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useQuery } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  XCircle,
  Clock,
  DollarSign,
  Target,
  Users,
  FileText,
  Calendar,
  Move,
  Settings2,
  Activity,
  Plus,
  Minus,
  ChevronDown,
  Briefcase,
  Receipt,
  SlidersHorizontal,
  HardDrive,
  AlertTriangle,
  Shield,
  Kanban,
  PlayCircle,
  PauseCircle,
  CheckCircle2,
  GripVertical,
  Building,
  User,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { secureApiRequest } from "@/lib/csrfClient";
import { useIsMobile } from "@/hooks/use-mobile";
import defaultLogo from "@assets/logo_1753388790228.png";
import { calculateNextReviewDate, formatBusinessDate, getDateAdjustmentReason } from "@/lib/businessDays";
import { createLocalDateTime, formatLocalDateTime } from "@/lib/timezoneUtils";
import { DEFAULT_PRESENTATION_TEMPLATE, SlideConfigPanel, type PresentationTemplate, type SlideConfig } from "./presentation-template-config";
import { QbrTicketSummarySlide } from "./qbr-ticket-summary-slide";

interface QbrPresentationProps {
  qbr: any;
  customer: any;
  onClose: () => void;
}

// Utility function to decode HTML entities
const decodeHtmlEntities = (text: string): string => {
  if (!text) return '';
  const textArea = document.createElement('textarea');
  textArea.textContent = text;
  return textArea.value;
};

// Utility function to format category names to human-friendly labels
const formatCategoryName = (category: string): string => {
  if (!category) return 'Uncategorized';
  
  // Handle common category formats
  const categoryMap: { [key: string]: string } = {
    'software_licenses': 'Software Licenses',
    'm365_licenses': 'Microsoft 365 Licenses',
    'office365_licenses': 'Office 365 Licenses',
    'microsoft_licenses': 'Microsoft Licenses',
    'hardware': 'Hardware',
    'cloud_services': 'Cloud Services',
    'security': 'Security',
    'networking': 'Networking',
    'infrastructure': 'Infrastructure',
    'backup_recovery': 'Backup & Recovery',
    'monitoring': 'Monitoring',
    'support_maintenance': 'Support & Maintenance',
    'support_contracts': 'Support Contracts',
    'telecommunications': 'Telecommunications',
    'professional_services': 'Professional Services',
    'training': 'Training',
    'compliance': 'Compliance',
    'msp_contract': 'MSP Contract',
    'msp_services': 'MSP Services',
    'rmm_licenses': 'RMM Licenses',
    'other': 'Other',
    'uncategorized': 'Uncategorized'
  };
  
  // Check if we have a direct mapping
  const lowerCategory = category.toLowerCase();
  if (categoryMap[lowerCategory]) {
    return categoryMap[lowerCategory];
  }
  
  // Convert snake_case or kebab-case to Title Case
  return category
    .replace(/[_-]/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Backup Status slide content for QBR presentations
function BackupStatusSlideContent({ customerId }: { customerId: number }) {
  const { data, isLoading } = useQuery<{
    configured: boolean;
    summary?: {
      totalDevices: number;
      successfulBackups: number;
      failedBackups: number;
      neverBackedUp: number;
      inProgress: number;
      successRate: number;
      totalSelectedSize: number;
      totalUsedStorage: number;
      totalErrors: number;
      totalWarnings: number;
      lastSyncedAt: string | null;
    };
    devices?: Array<{
      deviceName: string;
      deviceOs: string | null;
      lastSessionStatus: string | null;
      lastSuccessfulBackup: string | null;
      selectedSize: number | null;
      usedStorage: number | null;
    }>;
  }>({
    queryKey: [`/api/integrations/cove/customers/${customerId}/backup-stats`],
  });

  if (isLoading) {
    return <div className="text-center text-muted-foreground py-8">Loading backup data...</div>;
  }

  if (!data?.configured || !data?.summary) {
    return (
      <div className="text-center text-muted-foreground py-8">
        <Shield className="h-10 w-10 mx-auto mb-3 opacity-50" />
        <p>No backup data available for this customer</p>
        <p className="text-sm mt-1">Configure a backup integration to display backup statistics</p>
      </div>
    );
  }

  const { summary, devices = [] } = data;
  const formatBytes = (bytes: number | null) => {
    if (!bytes || bytes === 0) return "0 B";
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  };

  return (
    <div className="space-y-6">
      {/* Summary metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4 pb-3 text-center">
            <HardDrive className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
            <p className="text-2xl font-bold">{summary.totalDevices}</p>
            <p className="text-xs text-muted-foreground">Total Devices</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 text-center">
            <Shield className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
            <p className="text-2xl font-bold">{summary.successRate}%</p>
            <p className="text-xs text-muted-foreground">Success Rate</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 text-center">
            <CheckCircle className="h-5 w-5 mx-auto mb-1 text-green-500" />
            <p className="text-2xl font-bold text-green-600">{summary.successfulBackups}</p>
            <p className="text-xs text-muted-foreground">Successful</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 text-center">
            <XCircle className="h-5 w-5 mx-auto mb-1 text-red-500" />
            <p className="text-2xl font-bold text-red-600">{summary.failedBackups}</p>
            <p className="text-xs text-muted-foreground">Failed</p>
          </CardContent>
        </Card>
      </div>

      {/* Storage overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <div className="text-center">
          <p className="text-muted-foreground">Selected Size</p>
          <p className="font-semibold">{formatBytes(summary.totalSelectedSize)}</p>
        </div>
        <div className="text-center">
          <p className="text-muted-foreground">Used Storage</p>
          <p className="font-semibold">{formatBytes(summary.totalUsedStorage)}</p>
        </div>
        <div className="text-center">
          <p className="text-muted-foreground">Errors / Warnings</p>
          <p className="font-semibold">{summary.totalErrors} / {summary.totalWarnings}</p>
        </div>
        <div className="text-center">
          <p className="text-muted-foreground">Last Synced</p>
          <p className="font-semibold">{summary.lastSyncedAt ? new Date(summary.lastSyncedAt).toLocaleDateString() : "Never"}</p>
        </div>
      </div>

      {/* Device list (top 10 for slide brevity) */}
      {devices.length > 0 && (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Device</th>
                <th className="text-left px-3 py-2 font-medium">OS</th>
                <th className="text-left px-3 py-2 font-medium">Status</th>
                <th className="text-left px-3 py-2 font-medium">Last Backup</th>
                <th className="text-right px-3 py-2 font-medium">Storage</th>
              </tr>
            </thead>
            <tbody>
              {devices.slice(0, 10).map((device, idx) => (
                <tr key={idx} className="border-t">
                  <td className="px-3 py-2 font-medium">{device.deviceName}</td>
                  <td className="px-3 py-2 text-muted-foreground">{device.deviceOs || "—"}</td>
                  <td className="px-3 py-2">
                    <Badge variant={device.lastSessionStatus === "completed" ? "outline" : "destructive"} className={device.lastSessionStatus === "completed" ? "text-green-600 border-green-200" : ""}>
                      {device.lastSessionStatus === "completed" ? "Success" : device.lastSessionStatus === "failed" ? "Failed" : device.lastSessionStatus || "Unknown"}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {device.lastSuccessfulBackup ? new Date(device.lastSuccessfulBackup).toLocaleDateString() : "Never"}
                  </td>
                  <td className="px-3 py-2 text-right">{formatBytes(device.usedStorage)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {devices.length > 10 && (
            <div className="text-center text-xs text-muted-foreground py-2 bg-muted/30">
              Showing 10 of {devices.length} devices
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Patch Status slide content for QBR presentations (N-Central + NinjaOne)
function PatchStatusSlideContent({ customerId }: { customerId: number }) {
  const [rmmSource, setRmmSource] = useState<'ncentral' | 'ninjaone' | null>(null);
  const [nCentralData, setNCentralData] = useState<any>(null);
  const [ninjaOneData, setNinjaOneData] = useState<any>(null);

  // Check which RMM integrations are active
  const { data: integrations } = useQuery<any[]>({
    queryKey: ["/api/integrations"],
  });

  const activeNcentral = integrations?.find((i: any) => i.provider === 'ncentral' && i.status === 'active');
  const activeNinjaOne = integrations?.find((i: any) => i.provider === 'ninjaone' && i.status === 'active');

  const generateNCentralMutation = useMutation({
    mutationFn: async () => apiRequest("POST", "/api/integrations/ncentral/patch-report/generate"),
    onSuccess: (data) => { setNCentralData(data); setRmmSource('ncentral'); },
  });

  const generateNinjaOneMutation = useMutation({
    mutationFn: async () => apiRequest("POST", "/api/integrations/ninjaone/patch-report", {}),
    onSuccess: (data) => { setNinjaOneData(data); setRmmSource('ninjaone'); },
  });

  const isPending = generateNCentralMutation.isPending || generateNinjaOneMutation.isPending;

  // No data yet — show generate buttons
  if (!rmmSource && !isPending) {
    const hasRmm = activeNcentral || activeNinjaOne;
    return (
      <div className="text-center text-muted-foreground py-8 space-y-4">
        <Shield className="h-10 w-10 mx-auto opacity-50" />
        <p>{hasRmm ? "Generate a patch report to view patch status" : "No RMM integration configured"}</p>
        {!hasRmm && <p className="text-sm">Configure N-Central or NinjaOne to display patch data</p>}
        <div className="flex gap-2 justify-center">
          {activeNcentral && (
            <Button onClick={() => generateNCentralMutation.mutate()} variant="outline" size="sm">
              N-Central Report
            </Button>
          )}
          {activeNinjaOne && (
            <Button onClick={() => generateNinjaOneMutation.mutate()} variant="outline" size="sm">
              NinjaOne Report
            </Button>
          )}
        </div>
        {(generateNCentralMutation.isError || generateNinjaOneMutation.isError) && (
          <p className="text-sm text-red-500">
            {(generateNCentralMutation.error as any)?.message || (generateNinjaOneMutation.error as any)?.message || "Failed to generate patch report."}
          </p>
        )}
      </div>
    );
  }

  if (isPending) {
    return (
      <div className="text-center text-muted-foreground py-8">
        <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-3" />
        <p>Generating patch report...</p>
        <p className="text-sm mt-1">This may take a moment</p>
      </div>
    );
  }

  // N-Central data rendering
  if (rmmSource === 'ncentral' && nCentralData) {
    if (nCentralData.reportStatus === 'FAILED') {
      return (
        <div className="text-center text-muted-foreground py-8">
          <AlertTriangle className="h-10 w-10 mx-auto mb-3 text-red-500 opacity-70" />
          <p>Patch report generation failed</p>
          <p className="text-sm mt-1">{nCentralData.message}</p>
          <Button onClick={() => generateNCentralMutation.mutate()} variant="outline" size="sm" className="mt-3">Retry</Button>
        </div>
      );
    }

    const summaries = nCentralData?.data?.deviceClassSummaries || [];
    if (summaries.length === 0) {
      return (
        <div className="text-center text-muted-foreground py-8">
          <Shield className="h-10 w-10 mx-auto mb-3 opacity-50" />
          <p>No patch data returned from N-Central</p>
        </div>
      );
    }

    const totals = summaries.reduce(
      (acc: any, s: any) => ({
        approvedForInstall: acc.approvedForInstall + (s.approvalStatusCounts.approvedForInstall || 0),
        notApproved: acc.notApproved + (s.approvalStatusCounts.notApproved || 0),
        declined: acc.declined + (s.approvalStatusCounts.declined || 0),
        noApproval: acc.noApproval + (s.approvalStatusCounts.noApproval || 0),
        approvedForRemoval: acc.approvedForRemoval + (s.approvalStatusCounts.approvedForRemoval || 0),
      }),
      { approvedForInstall: 0, notApproved: 0, declined: 0, noApproval: 0, approvedForRemoval: 0 }
    );
    const totalPatches = totals.approvedForInstall + totals.notApproved + totals.declined + totals.noApproval + totals.approvedForRemoval;

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card><CardContent className="pt-4 pb-3 text-center"><p className="text-2xl font-bold">{totalPatches}</p><p className="text-xs text-muted-foreground">Total Patches</p></CardContent></Card>
          <Card><CardContent className="pt-4 pb-3 text-center"><CheckCircle className="h-5 w-5 mx-auto mb-1 text-green-500" /><p className="text-2xl font-bold text-green-600">{totals.approvedForInstall}</p><p className="text-xs text-muted-foreground">Approved</p></CardContent></Card>
          <Card><CardContent className="pt-4 pb-3 text-center"><Clock className="h-5 w-5 mx-auto mb-1 text-yellow-500" /><p className="text-2xl font-bold text-yellow-600">{totals.notApproved}</p><p className="text-xs text-muted-foreground">Not Approved</p></CardContent></Card>
          <Card><CardContent className="pt-4 pb-3 text-center"><XCircle className="h-5 w-5 mx-auto mb-1 text-red-500" /><p className="text-2xl font-bold text-red-600">{totals.declined}</p><p className="text-xs text-muted-foreground">Declined</p></CardContent></Card>
          <Card><CardContent className="pt-4 pb-3 text-center"><p className="text-2xl font-bold text-muted-foreground">{totals.noApproval + totals.approvedForRemoval}</p><p className="text-xs text-muted-foreground">Other</p></CardContent></Card>
        </div>
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Device Class</th>
                <th className="text-right px-3 py-2 font-medium">Approved</th>
                <th className="text-right px-3 py-2 font-medium">Not Approved</th>
                <th className="text-right px-3 py-2 font-medium">Declined</th>
                <th className="text-right px-3 py-2 font-medium">No Approval</th>
                <th className="text-right px-3 py-2 font-medium">For Removal</th>
              </tr>
            </thead>
            <tbody>
              {summaries.map((summary: any, idx: number) => (
                <tr key={idx} className="border-t">
                  <td className="px-3 py-2 font-medium">{summary.deviceClass}</td>
                  <td className="px-3 py-2 text-right text-green-600">{summary.approvalStatusCounts.approvedForInstall || 0}</td>
                  <td className="px-3 py-2 text-right text-yellow-600">{summary.approvalStatusCounts.notApproved || 0}</td>
                  <td className="px-3 py-2 text-right text-red-600">{summary.approvalStatusCounts.declined || 0}</td>
                  <td className="px-3 py-2 text-right text-muted-foreground">{summary.approvalStatusCounts.noApproval || 0}</td>
                  <td className="px-3 py-2 text-right text-muted-foreground">{summary.approvalStatusCounts.approvedForRemoval || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // NinjaOne data rendering
  if (rmmSource === 'ninjaone' && ninjaOneData) {
    const osTotal = ninjaOneData.osPatchInstalls?.total || 0;
    const osInstalled = ninjaOneData.osPatchInstalls?.installed || 0;
    const osFailed = ninjaOneData.osPatchInstalls?.failed || 0;
    const swTotal = ninjaOneData.softwarePatchInstalls?.total || 0;
    const swInstalled = ninjaOneData.softwarePatchInstalls?.installed || 0;
    const swFailed = ninjaOneData.softwarePatchInstalls?.failed || 0;
    const grandTotal = osTotal + swTotal;

    if (grandTotal === 0) {
      return (
        <div className="text-center text-muted-foreground py-8">
          <Shield className="h-10 w-10 mx-auto mb-3 opacity-50" />
          <p>No patch installation data returned from NinjaOne</p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card><CardContent className="pt-4 pb-3 text-center"><p className="text-2xl font-bold">{grandTotal}</p><p className="text-xs text-muted-foreground">Total Patches</p></CardContent></Card>
          <Card><CardContent className="pt-4 pb-3 text-center"><CheckCircle className="h-5 w-5 mx-auto mb-1 text-green-500" /><p className="text-2xl font-bold text-green-600">{osInstalled + swInstalled}</p><p className="text-xs text-muted-foreground">Installed</p></CardContent></Card>
          <Card><CardContent className="pt-4 pb-3 text-center"><XCircle className="h-5 w-5 mx-auto mb-1 text-red-500" /><p className="text-2xl font-bold text-red-600">{osFailed + swFailed}</p><p className="text-xs text-muted-foreground">Failed</p></CardContent></Card>
          <Card><CardContent className="pt-4 pb-3 text-center"><Shield className="h-5 w-5 mx-auto mb-1 text-muted-foreground" /><p className="text-2xl font-bold">{grandTotal > 0 ? Math.round(((osInstalled + swInstalled) / grandTotal) * 100) : 0}%</p><p className="text-xs text-muted-foreground">Success Rate</p></CardContent></Card>
        </div>

        {/* OS Patches by Severity */}
        {ninjaOneData.osPatchesBySeverity && Object.keys(ninjaOneData.osPatchesBySeverity).length > 0 && (
          <div>
            <h4 className="font-medium text-sm mb-2">OS Patches by Severity</h4>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">Severity</th>
                    <th className="text-right px-3 py-2 font-medium">Installed</th>
                    <th className="text-right px-3 py-2 font-medium">Failed</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(ninjaOneData.osPatchesBySeverity).map(([sev, counts]: [string, any]) => (
                    <tr key={sev} className="border-t">
                      <td className="px-3 py-2 font-medium">{sev}</td>
                      <td className="px-3 py-2 text-right text-green-600">{counts.installed}</td>
                      <td className="px-3 py-2 text-right text-red-600">{counts.failed}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Software Patches by Impact */}
        {ninjaOneData.softwarePatchesByImpact && Object.keys(ninjaOneData.softwarePatchesByImpact).length > 0 && (
          <div>
            <h4 className="font-medium text-sm mb-2">Software Patches by Impact</h4>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">Impact</th>
                    <th className="text-right px-3 py-2 font-medium">Installed</th>
                    <th className="text-right px-3 py-2 font-medium">Failed</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(ninjaOneData.softwarePatchesByImpact).map(([imp, counts]: [string, any]) => (
                    <tr key={imp} className="border-t">
                      <td className="px-3 py-2 font-medium">{imp}</td>
                      <td className="px-3 py-2 text-right text-green-600">{counts.installed}</td>
                      <td className="px-3 py-2 text-right text-red-600">{counts.failed}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
}

export default function QbrPresentation({
  qbr,
  customer,
  onClose,
}: QbrPresentationProps) {
  const { toast } = useToast();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [approvals, setApprovals] = useState<
    Record<number, "approved" | "rejected" | "deferred">
  >({});
  const [meetingNotes, setMeetingNotes] = useState(qbr.meetingNotes || "");
  const [showSummary, setShowSummary] = useState(false);
  const [assigningRecommendation, setAssigningRecommendation] =
    useState<any>(null);
  const [draggedItem, setDraggedItem] = useState<any>(null);
  const [dropZoneActive, setDropZoneActive] = useState<string | null>(null);
  const [quarterStartIndex, setQuarterStartIndex] = useState(0);
  const [optimisticUpdates, setOptimisticUpdates] = useState<{[key: string]: { quarter: string; month: string | null }}>({});
  const [collapsedSections, setCollapsedSections] = useState<{
    [quarter: string]: { projects: boolean; expenses: boolean };
  }>({});
  const [assetReplacements, setAssetReplacements] = useState<any[]>([]); // Track asset replacement recommendations
  const QUARTERS_TO_SHOW = 4; // 1 year worth of quarters
  
  // Track pending slide position adjustments to maintain user's position when slides change
  const pendingSlideAdjustmentRef = useRef<number>(0);
  
  // Timeframe selector state for Planning Board (months/quarters/years)
  const [planningTimeframe, setPlanningTimeframe] = useState<"months" | "quarters" | "years">("quarters");
  
  // Slide configuration state
  const [showSlideConfig, setShowSlideConfig] = useState(false);
  const [localPresentationConfig, setLocalPresentationConfig] = useState<PresentationTemplate | null>(null);
  
  // Implementation tracking drag-and-drop state
  const [implDraggedItem, setImplDraggedItem] = useState<any>(null);
  
  // Yearly budget editing state
  const [editingYearBudget, setEditingYearBudget] = useState<{ year: string; budget: number } | null>(null);
  
  // Next review scheduling state
  const [nextReviewDate, setNextReviewDate] = useState<string>("");
  const [nextReviewTime, setNextReviewTime] = useState<string>("");
  const [dateAdjustmentReason, setDateAdjustmentReason] = useState<string | null>(null);
  const [timeWarning, setTimeWarning] = useState<string | null>(null);
  
  // Next review creation confirmation dialog
  const [showNextReviewConfirmation, setShowNextReviewConfirmation] = useState(false);
  const [isCreatingNextReview, setIsCreatingNextReview] = useState(false);
  
  // Validate time is within business hours (9 AM - 5 PM)
  const handleTimeChange = (newTime: string) => {
    setNextReviewTime(newTime);
    
    // Parse hour from time string (HH:MM format)
    const [hours] = newTime.split(':').map(Number);
    
    if (hours < 9) {
      setTimeWarning("Selected time is before business hours (9 AM - 5 PM)");
    } else if (hours >= 17) {
      setTimeWarning("Selected time is after business hours (9 AM - 5 PM)");
    } else {
      setTimeWarning(null);
    }
  };
  
  // Initialize time for NEXT review (default to same time as current meeting, or 10:00 AM)
  useEffect(() => {
    if (qbr?.scheduledMeetingDate) {
      // Use the same time as the current meeting for the next review
      const scheduledDate = new Date(qbr.scheduledMeetingDate);
      const hours = scheduledDate.getHours().toString().padStart(2, '0');
      const minutes = scheduledDate.getMinutes().toString().padStart(2, '0');
      const timeString = `${hours}:${minutes}`;
      setNextReviewTime(timeString);
      
      // Validate the time
      const [hourNum] = timeString.split(':').map(Number);
      if (hourNum < 9) {
        setTimeWarning("Selected time is before business hours (9 AM - 5 PM)");
      } else if (hourNum >= 17) {
        setTimeWarning("Selected time is after business hours (9 AM - 5 PM)");
      } else {
        setTimeWarning(null);
      }
    } else {
      // Default to 10:00 AM if no scheduled time exists
      setNextReviewTime("10:00");
      setTimeWarning(null);
    }
  }, [qbr]);
  
  // Calculate suggested next review date based on customer cadence
  // Excludes weekends and US federal holidays
  useEffect(() => {
    if (customer?.reviewFrequency) {
      const cadence = customer.reviewFrequency as 'monthly' | 'quarterly' | 'biannual' | 'annual';
      
      // Calculate the raw date based on cadence
      const today = new Date();
      const rawDate = new Date(today);
      
      switch (cadence) {
        case "monthly":
          rawDate.setDate(rawDate.getDate() + 30);
          break;
        case "quarterly":
          rawDate.setDate(rawDate.getDate() + 90);
          break;
        case "biannual":
          rawDate.setDate(rawDate.getDate() + 180);
          break;
        case "annual":
          rawDate.setDate(rawDate.getDate() + 365);
          break;
        default:
          rawDate.setDate(rawDate.getDate() + 90);
      }
      
      // Adjust to next business day (skipping weekends and holidays)
      const suggestedDate = calculateNextReviewDate(null, cadence);
      
      // Check if date was adjusted
      const adjustmentReason = getDateAdjustmentReason(rawDate, suggestedDate);
      setDateAdjustmentReason(adjustmentReason);
      
      // Format as YYYY-MM-DD for input
      const formattedDate = suggestedDate.toISOString().split('T')[0];
      setNextReviewDate(formattedDate);
    }
  }, [customer]);

  // Fetch fresh QBR data to ensure we have the latest recommendations
  const { data: freshQbrData } = useQuery<any>({
    queryKey: ["/api/qbrs", qbr.id],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/qbrs/${qbr.id}`);
      return response;
    },
  });

  // Use fresh QBR data if available, otherwise fall back to prop
  const currentQbr = freshQbrData || qbr;

  // Initialize approvals from existing data
  useEffect(() => {
    if (currentQbr.customerApprovals) {
      const existingApprovals: Record<
        number,
        "approved" | "rejected" | "deferred"
      > = {};
      currentQbr.customerApprovals.forEach((approval: any, index: number) => {
        existingApprovals[index] = approval.status;
      });
      setApprovals(existingApprovals);
    }
  }, [currentQbr]);

  const recommendations = currentQbr.recommendations || [];

  // Fetch all QBR recommendations for comprehensive budget planning
  const { data: qbrRecommendations = [] } = useQuery<any[]>({
    queryKey: ["/api/qbr-recommendations"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/qbr-recommendations");
      return response;
    },
  });

  // Get all QBRs for this customer
  const { data: allQbrs = [] } = useQuery<any[]>({
    queryKey: ["/api/qbrs"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/qbrs");
      return response;
    },
  });

  // Filter QBRs for the current customer
  const customerQbrs = allQbrs.filter(
    (qbr: any) => qbr.customerId === customer?.id,
  );

  // Fetch IT expenses (budget line items) for this customer
  const { data: lineItems = [] } = useQuery<any[]>({
    queryKey: [`/api/customers/${customer?.id}/budget-line-items`],
    enabled: !!customer?.id,
    retry: 2,
    staleTime: 10000,
  });

  // Fetch PSA mapping for the customer to get ConnectWise company ID
  const { data: psaMappings = [] } = useQuery<any[]>({
    queryKey: ["/api/customers/psa-mappings"],
    enabled: !!customer?.id,
  });

  // Find PSA company ID for this customer
  const customerPsaMapping = psaMappings.find(
    (m: any) => m.customerId === customer?.id
  );
  const psaCompanyId = customerPsaMapping?.psaCustomerId;

  // Fetch customer assets for lifecycle review
  const { data: customerAssets = [] } = useQuery<any[]>({
    queryKey: [`/api/assets/customer/${customer?.id}`],
    queryFn: () => apiRequest("GET", `/api/assets/customer/${customer?.id}`),
    enabled: !!customer?.id,
  });

  // Get included assets from QBR (if specified) or fall back to assets needing attention
  const includedAssetIds: number[] = currentQbr.includedAssets || [];

  // Calculate asset lifecycle metrics
  const assetLifecycleMetrics = (() => {
    const now = new Date();
    const ninetyDaysFromNow = new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000);
    
    // If QBR has specific included assets, use those; otherwise show assets needing attention
    const assetsToShow = includedAssetIds.length > 0
      ? customerAssets.filter((asset: any) => includedAssetIds.includes(asset.id))
      : customerAssets.filter((asset: any) => {
          // Check warranty expiring within 90 days
          if (asset.warrantyEndDate) {
            const warrantyEnd = new Date(asset.warrantyEndDate);
            if (warrantyEnd > now && warrantyEnd <= ninetyDaysFromNow) return true;
            if (warrantyEnd <= now) return true; // Expired warranty
          }
          // Check aging assets (3+ years old)
          if (asset.purchaseDate) {
            const purchaseDate = new Date(asset.purchaseDate);
            const ageYears = (now.getTime() - purchaseDate.getTime()) / (1000 * 60 * 60 * 24 * 365);
            if (ageYears >= 3) return true;
          }
          // Check EOL approaching
          if (asset.endOfLifeDate) {
            const eolDate = new Date(asset.endOfLifeDate);
            if (eolDate <= ninetyDaysFromNow) return true;
          }
          // Check lifecycle stage
          if (asset.lifecycleStage === 'aging' || asset.lifecycleStage === 'end_of_life') return true;
          return false;
        });

    const warrantyExpiring = assetsToShow.filter((asset: any) => {
      if (!asset.warrantyEndDate) return false;
      const warrantyEnd = new Date(asset.warrantyEndDate);
      return warrantyEnd > now && warrantyEnd <= ninetyDaysFromNow;
    });

    const warrantyExpired = assetsToShow.filter((asset: any) => {
      if (!asset.warrantyEndDate) return false;
      const warrantyEnd = new Date(asset.warrantyEndDate);
      return warrantyEnd <= now;
    });

    const agingAssets = assetsToShow.filter((asset: any) => {
      return asset.lifecycleStage === 'aging' || asset.lifecycleStage === 'end_of_life';
    });

    const qbrAssetCosts = currentQbr.assetReplacementCosts || {};
    const totalReplacementCost = assetsToShow.reduce((sum: number, asset: any) => {
      const qbrCost = qbrAssetCosts[asset.id];
      const cost = qbrCost ? parseFloat(qbrCost) : (parseFloat(asset.replacementCost) || 0);
      return sum + cost;
    }, 0);

    return {
      total: customerAssets.length,
      needingAttention: assetsToShow,
      warrantyExpiring,
      warrantyExpired,
      agingAssets,
      totalReplacementCost,
    };
  })();

  // Get ALL recommendations from ALL customer QBRs
  const allCustomerRecommendations = customerQbrs.flatMap((qbr: any) => {
    const recs = qbr.recommendations || [];

    return recs.map((rec: any, index: number) => {
      // For current QBR, use the approval status
      let status = "approved"; // Default for older QBRs
      if (qbr.id === currentQbr.id) {
        // If the recommendation already has a status (like asset replacements), use it
        if (rec.status && rec.status !== "pending") {
          status = rec.status;
        } else {
          const approval = approvals[index];
          status = approval || "pending";
        }
      }

      return {
        id: rec.id || `${qbr.id}-${index}`,
        title: rec.title,
        description: rec.description,
        priority: rec.priority,
        timeline: rec.timeline,
        customerId: customer?.id,
        qbrId: qbr.id,
        index: index, // Add index to track original position
        status: status,
        budgetAllocated: rec.estimatedCost || rec.budgetAllocated || 10000,
        targetQuarter: rec.targetQuarter || null,
        targetMonth: rec.targetMonth || null,
        category: rec.category || "General",
        qbrTitle: qbr.title,
        quarter: qbr.reviewPeriod || qbr.quarter,
      };
    });
  });

  // Combine QBR recommendations with asset replacement recommendations
  // Filter out asset replacements that already exist in QBR recommendations to avoid duplicates
  const existingRecIds = new Set(allCustomerRecommendations.map((rec: any) => rec.id.toString()));
  const filteredAssetReplacements = assetReplacements.filter((rec: any) => !existingRecIds.has(rec.id.toString()));
  const allRecommendationsWithAssets = [...allCustomerRecommendations, ...filteredAssetReplacements];

  // Apply optimistic updates to recommendations for immediate UI feedback
  const effectiveRecommendations = allRecommendationsWithAssets.map((rec: any) => {
    const optimisticUpdate = optimisticUpdates[rec.id];
    return {
      ...rec,
      targetQuarter: optimisticUpdate?.quarter || rec.targetQuarter,
      targetMonth: optimisticUpdate?.month !== undefined ? optimisticUpdate.month : rec.targetMonth
    };
  });

  // Helper function to get expense amount for a quarter
  const getExpenseAmountForQuarter = (expense: any) => {
    const amount = parseFloat(expense.amount) || 0;
    const frequency = (expense.frequency || 'monthly').toLowerCase();
    
    switch (frequency) {
      case 'monthly':
        return amount * 3; // 3 months per quarter
      case 'quarterly':
        return amount;
      case 'annual':
      case 'annually':
      case 'yearly':
        return amount; // Full amount in the applicable quarter
      case 'one-time':
      case 'onetime':
      case 'one_time':
        return amount; // Full amount in the applicable quarter
      default:
        return amount; // Default to showing the amount
    }
  };

  // Helper function to get expense amount for a single month
  const getExpenseAmountForMonth = (expense: any) => {
    const amount = parseFloat(expense.amount) || 0;
    const frequency = (expense.frequency || 'monthly').toLowerCase();
    
    switch (frequency) {
      case 'monthly':
        return amount; // Monthly amount as-is
      case 'quarterly':
        return amount; // Full quarterly amount in the month it appears
      case 'annual':
      case 'annually':
      case 'yearly':
        return amount; // Full annual amount in the month it appears
      case 'one-time':
      case 'onetime':
      case 'one_time':
        return amount; // Full amount in the month it appears
      default:
        return amount; // Default to showing the amount
    }
  };

  // Helper function to get expense amount for a year (consolidates quarterly/monthly into yearly total)
  const getExpenseAmountForYear = (expense: any, quartersInYear: number = 4) => {
    const amount = parseFloat(expense.amount) || 0;
    const frequency = (expense.frequency || 'monthly').toLowerCase();
    
    switch (frequency) {
      case 'monthly':
        return amount * 12; // 12 months per year
      case 'quarterly':
        return amount * quartersInYear; // 4 quarters per year (or fewer if partial year)
      case 'annual':
      case 'annually':
      case 'yearly':
        return amount; // Full amount once per year
      case 'one-time':
      case 'onetime':
      case 'one_time':
        return amount; // Full amount once
      default:
        return amount; // Default to showing the amount
    }
  };

  // Helper function to consolidate expenses by ID for yearly view
  const consolidateExpensesForYear = (expenses: any[]) => {
    const expenseMap = new Map<string, any>();
    
    expenses.forEach(expense => {
      // Use expense.id if available, otherwise create a composite key
      const key = expense.id 
        ? String(expense.id)
        : `${expense.name || ''}-${expense.vendor || ''}-${expense.category || ''}-${expense.amount || ''}-${expense.startDate || ''}`;
      
      if (!expenseMap.has(key)) {
        // First occurrence - store with yearly amount (always use 4 quarters for full year view)
        expenseMap.set(key, {
          ...expense,
          yearlyAmount: getExpenseAmountForYear(expense, 4),
        });
      }
      // Skip duplicates - we only need one entry per expense for yearly view
    });
    
    return Array.from(expenseMap.values());
  };

  // Helper function to allocate expenses to quarters
  const allocateExpensesToQuarters = (expenses: any[], quarters: string[]) => {
    const allocation = new Map<string, any[]>();
    
    quarters.forEach(quarter => {
      allocation.set(quarter, []);
    });

    expenses.forEach(expense => {
      const startDate = expense.startDate ? new Date(expense.startDate) : new Date();
      const renewalDate = expense.renewalDate ? new Date(expense.renewalDate) : null;
      
      quarters.forEach(quarter => {
        // Parse quarter string (e.g., "Q1 2025")
        const [q, yearStr] = quarter.split(' ');
        const year = parseInt(yearStr, 10);
        const quarterNum = parseInt(q.substring(1), 10);
        const quarterStartMonth = (quarterNum - 1) * 3;
        const quarterEndMonth = quarterStartMonth + 2;
        
        const quarterStart = new Date(year, quarterStartMonth, 1);
        const quarterEnd = new Date(year, quarterEndMonth + 1, 0);

        // Check if expense applies to this quarter
        let shouldInclude = false;

        if (expense.frequency === 'monthly' || expense.frequency === 'quarterly') {
          // Recurring expenses: include if active during quarter
          shouldInclude = startDate <= quarterEnd;
        } else if (expense.frequency === 'annual' && renewalDate) {
          // Annual expenses: include in renewal quarter
          const renewalQuarter = Math.ceil((renewalDate.getMonth() + 1) / 3);
          const renewalYear = renewalDate.getFullYear();
          shouldInclude = quarterNum === renewalQuarter && year === renewalYear;
        } else if (expense.frequency === 'one_time') {
          // One-time expenses: include in start quarter
          const startQuarter = Math.ceil((startDate.getMonth() + 1) / 3);
          const startYear = startDate.getFullYear();
          shouldInclude = quarterNum === startQuarter && year === startYear;
        }

        if (shouldInclude) {
          allocation.get(quarter)?.push(expense);
        }
      });
    });

    return allocation;
  };

  // Budget calculation functions - current and future quarters (dynamically generated)
  const getAllQuarters = () => {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();
    const currentQuarter = Math.floor(currentMonth / 3) + 1;
    const quarters: string[] = [];
    
    // Start from current quarter and generate 12 future quarters
    let quarterCount = 0;
    for (let yearOffset = 0; yearOffset < 4 && quarterCount < 12; yearOffset++) {
      const year = currentYear + yearOffset;
      const startQuarter = yearOffset === 0 ? currentQuarter : 1;
      
      for (let q = startQuarter; q <= 4 && quarterCount < 12; q++) {
        quarters.push(`Q${q} ${year}`);
        quarterCount++;
      }
    }
    
    return quarters;
  };

  const getQuarterlyBudgets = () => {
    const quarters = getAllQuarters();
    const quarterExpenseAllocation = allocateExpensesToQuarters(lineItems, quarters);
    
    return quarters.map((quarter) => {
      const quarterRecs = effectiveRecommendations.filter(
        (rec: any) => rec.targetQuarter === quarter,
      );
      const quarterExpenses = quarterExpenseAllocation.get(quarter) || [];
      
      // Calculate project totals
      const projectsAllocated = quarterRecs.reduce(
        (sum: number, rec: any) => sum + (parseFloat(rec.budgetAllocated) || 0),
        0,
      );
      const projectsActual = quarterRecs.reduce(
        (sum: number, rec: any) => sum + (parseFloat(rec.actualCost) || 0),
        0,
      );
      
      // Calculate expense totals
      const expensesAllocated = quarterExpenses.reduce(
        (sum: number, expense: any) => sum + getExpenseAmountForQuarter(expense),
        0,
      );
      
      const totalAllocated = projectsAllocated + expensesAllocated;
      const totalActual = projectsActual; // Only actual project costs, not planned expenses

      return {
        quarter,
        allocated: totalAllocated,
        actual: totalActual,
        remaining: totalAllocated - totalActual,
        recommendations: quarterRecs,
        expenses: quarterExpenses,
        projectsAllocated,
        projectsActual,
        expensesAllocated,
      };
    });
  };

  // Annual budget breakdown by year
  const getAnnualBudgetBreakdown = () => {
    const quarterlyBudgets = getQuarterlyBudgets();
    const years = ['2025', '2026', '2027'];

    return years.map(year => {
      const yearQuarters = quarterlyBudgets.filter(q => q.quarter.includes(year));
      const allocated = yearQuarters.reduce((sum, q) => sum + q.allocated, 0);
      const actual = yearQuarters.reduce((sum, q) => sum + q.actual, 0);
      const projectCount = yearQuarters.reduce((sum, q) => sum + q.recommendations.length, 0);

      return {
        year,
        allocated,
        actual,
        remaining: allocated - actual,
        projectCount
      };
    });
  };

  // Generate months for allocation (36 months = 3 years)
  const getAllMonths = () => {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthsList: string[] = [];
    
    for (let monthOffset = 0; monthOffset < 36; monthOffset++) {
      const totalMonths = currentMonth + monthOffset;
      const year = currentYear + Math.floor(totalMonths / 12);
      const monthIndex = totalMonths % 12;
      monthsList.push(`${monthNames[monthIndex]} ${year}`);
    }
    
    return monthsList;
  };

  // Generate years for allocation (3 years)
  const getAllYears = () => {
    const currentYear = new Date().getFullYear();
    return [currentYear, currentYear + 1, currentYear + 2].map(y => y.toString());
  };

  // Get monthly budgets with expenses
  const getMonthlyBudgets = () => {
    const quarterlyBudgets = getQuarterlyBudgets();
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthlyData: any[] = [];
    
    quarterlyBudgets.forEach((quarterData) => {
      const [q, yearStr] = quarterData.quarter.split(' ');
      const year = parseInt(yearStr, 10);
      const quarterNum = parseInt(q.substring(1), 10);
      const startMonthIndex = (quarterNum - 1) * 3;
      
      // Generate 3 months for each quarter
      for (let i = 0; i < 3; i++) {
        const monthIndex = startMonthIndex + i;
        const monthLabel = `${monthNames[monthIndex]} ${year}`;
        const isFirstMonthOfQuarter = i === 0;
        
        // Calculate monthly expenses
        let monthlyExpensesAllocated = 0;
        const monthlyExpenses = (quarterData.expenses || []).filter((expense: any) => {
          const frequency = expense.frequency?.toLowerCase() || 'monthly';
          
          switch (frequency) {
            case 'monthly':
              return true; // Monthly expenses appear every month
            case 'quarterly':
              return isFirstMonthOfQuarter; // Quarterly expenses only in first month
            case 'annual':
            case 'annually':
            case 'yearly':
              const renewalDate = expense.renewalDate ? new Date(expense.renewalDate) : null;
              if (renewalDate) {
                return renewalDate.getMonth() === monthIndex && renewalDate.getFullYear() === year;
              }
              return monthIndex === 0; // Default to January
            case 'one-time':
            case 'onetime':
            case 'one_time':
              const startDate = expense.startDate ? new Date(expense.startDate) : null;
              if (startDate) {
                return startDate.getMonth() === monthIndex && startDate.getFullYear() === year;
              }
              return false;
            default:
              return true;
          }
        });
        
        monthlyExpensesAllocated = monthlyExpenses.reduce((sum: number, expense: any) => {
          const amount = parseFloat(expense.amount) || 0;
          const frequency = expense.frequency?.toLowerCase() || 'monthly';
          
          switch (frequency) {
            case 'monthly':
              return sum + amount;
            case 'quarterly':
            case 'annual':
            case 'annually':
            case 'yearly':
            case 'one-time':
            case 'onetime':
            case 'one_time':
              return sum + amount;
            default:
              return sum + amount;
          }
        }, 0);
        
        // Filter recommendations for this specific month
        // If a recommendation has targetMonth set, show it in that month
        // Otherwise, show it in the first month of its quarter (backward compatibility)
        let monthlyRecs: any[] = [];
        let monthlyProjectsAllocated = 0;
        
        if (quarterData && quarterData.recommendations) {
          // Filter recommendations that are specifically assigned to this month
          const monthSpecificRecs = quarterData.recommendations.filter((rec: any) => 
            rec.targetMonth === monthLabel
          );
          
          // For first month of quarter, also include recommendations without a specific month assignment
          // (backward compatibility for existing data)
          const quarterDefaultRecs = isFirstMonthOfQuarter 
            ? quarterData.recommendations.filter((rec: any) => !rec.targetMonth)
            : [];
          
          monthlyRecs = [...monthSpecificRecs, ...quarterDefaultRecs];
          monthlyProjectsAllocated = monthlyRecs.reduce(
            (sum: number, rec: any) => sum + (parseFloat(rec.budgetAllocated) || 0),
            0
          );
        }
        
        const monthlyAllocated = monthlyProjectsAllocated + monthlyExpensesAllocated;
        const monthlyActual = monthlyRecs.reduce(
          (sum: number, rec: any) => sum + (parseFloat(rec.actualCost) || 0),
          0
        );
        
        // Add monthlyAmount to each expense for proper display in monthly view
        const expensesWithMonthlyAmount = monthlyExpenses.map((expense: any) => ({
          ...expense,
          monthlyAmount: getExpenseAmountForMonth(expense),
        }));
        
        monthlyData.push({
          period: monthLabel,
          quarter: quarterData.quarter,
          displayLabel: monthLabel,
          isFirstMonthOfQuarter,
          isContinuationMonth: !isFirstMonthOfQuarter && monthlyExpenses.length === 0 && monthlyRecs.length === 0,
          allocated: monthlyAllocated,
          actual: monthlyActual,
          remaining: monthlyAllocated - monthlyActual,
          recommendations: monthlyRecs,
          projectsAllocated: monthlyProjectsAllocated,
          projectsActual: monthlyActual,
          expensesAllocated: monthlyExpensesAllocated,
          expenses: expensesWithMonthlyAmount,
        });
      }
    });
    
    return monthlyData;
  };

  // Get yearly budgets for board view
  const getYearlyBudgets = () => {
    const quarterlyBudgets = getQuarterlyBudgets();
    const years = getAllYears();
    
    return years.map(year => {
      const yearQuarters = quarterlyBudgets.filter(q => q.quarter.includes(year));
      const yearlyBudget = customer?.yearlyBudgets?.[year] || 0;
      const allocated = yearQuarters.reduce((sum, q) => sum + q.allocated, 0);
      const actual = yearQuarters.reduce((sum, q) => sum + q.actual, 0);
      const projectsAllocated = yearQuarters.reduce((sum, q) => sum + (q.projectsAllocated || 0), 0);
      const expensesAllocated = yearQuarters.reduce((sum, q) => sum + (q.expensesAllocated || 0), 0);
      
      // Consolidate expenses by ID for yearly view - recurring expenses should show once with yearly total
      const allYearExpenses = yearQuarters.flatMap(q => q.expenses || []);
      const consolidatedExpenses = consolidateExpensesForYear(allYearExpenses);
      
      return {
        period: year,
        displayLabel: year,
        quarter: year,
        dragDropQuarter: `Q1 ${year}`, // Default to Q1 when dropping on a year
        allocated,
        actual,
        remaining: allocated - actual,
        recommendations: yearQuarters.flatMap(q => q.recommendations || []),
        projectsAllocated,
        projectsActual: actual,
        expensesAllocated,
        expenses: consolidatedExpenses,
        yearlyBudget,
        isOverBudget: yearlyBudget > 0 && allocated > yearlyBudget,
      };
    });
  };

  // Get planning board data based on selected timeframe
  const getPlanningBoardData = () => {
    switch (planningTimeframe) {
      case 'months':
        return getMonthlyBudgets().map(m => ({
          ...m,
          dragDropQuarter: m.quarter,
          targetMonth: m.period, // Specific month for month-level assignment
        }));
      case 'years':
        return getYearlyBudgets().map(y => ({
          ...y,
          targetMonth: null,
        }));
      case 'quarters':
      default:
        return getQuarterlyBudgets().map(q => ({
          ...q,
          displayLabel: q.quarter,
          dragDropQuarter: q.quarter,
          targetMonth: null, // Quarters view doesn't use month-level assignment
        }));
    }
  };

  const getVisibleQuarters = () => {
    const quarters = getAllQuarters();
    return quarters.slice(quarterStartIndex, quarterStartIndex + QUARTERS_TO_SHOW);
  };

  const canNavigatePrevious = () => quarterStartIndex > 0;
  const canNavigateNext = () => {
    const quarters = getAllQuarters();
    return quarterStartIndex + QUARTERS_TO_SHOW < quarters.length;
  };

  const navigatePrevious = () => {
    if (canNavigatePrevious()) {
      setQuarterStartIndex(Math.max(0, quarterStartIndex - 1)); // Move back 1 quarter
    }
  };

  const navigateNext = () => {
    if (canNavigateNext()) {
      const quarters = getAllQuarters();
      setQuarterStartIndex(Math.min(quarters.length - QUARTERS_TO_SHOW, quarterStartIndex + 1)); // Move forward 1 quarter
    }
  };

  // Filter unassigned recommendations (only approved ones)
  const unassignedRecommendations = effectiveRecommendations.filter(
    (rec: any) => !rec.targetQuarter && rec.status === 'approved',
  );

  // Toggle section collapse state
  const toggleSection = (quarter: string, section: 'projects' | 'expenses') => {
    setCollapsedSections(prev => ({
      ...prev,
      [quarter]: {
        ...prev[quarter],
        [section]: !prev[quarter]?.[section],
      },
    }));
  };

  // Check if section is collapsed (default: both expanded)
  const isSectionCollapsed = (quarter: string, section: 'projects' | 'expenses'): boolean => {
    return collapsedSections[quarter]?.[section] || false;
  };

  // Drag and drop state (already declared above)

  // Touch drag and drop state
  const [touchStartPos, setTouchStartPos] = useState<{ x: number; y: number } | null>(null);
  const [touchDraggedItem, setTouchDraggedItem] = useState<any>(null);
  const isMobile = useIsMobile();

  // Drag and drop handlers
  const handleDragStart = (
    e: React.DragEvent,
    recommendation: any,
    sourceQuarter: string,
  ) => {
    const dragData = {
      recommendationId: recommendation.id,
      sourceQuarter,
      recommendation,
    };
    e.dataTransfer.setData("text/plain", JSON.stringify(dragData));
    setDraggedItem(recommendation);

    // Add visual feedback
    e.dataTransfer.effectAllowed = "move";
    const dragImage = document.createElement("div");
    dragImage.textContent = recommendation.title;
    dragImage.style.position = "absolute";
    dragImage.style.top = "-1000px";
    dragImage.style.background = "white";
    dragImage.style.padding = "8px";
    dragImage.style.borderRadius = "4px";
    dragImage.style.boxShadow = "0 4px 8px rgba(0,0,0,0.2)";
    document.body.appendChild(dragImage);
    e.dataTransfer.setDragImage(dragImage, 0, 0);
    setTimeout(() => document.body.removeChild(dragImage), 0);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDragEnter = (e: React.DragEvent, quarter: string) => {
    e.preventDefault();
    setDropZoneActive(quarter);
  };

  const handleDragLeave = () => {
    setDropZoneActive(null);
  };

  const handleDrop = (e: React.DragEvent, targetQuarter: string, targetMonth?: string) => {
    e.preventDefault();
    setDropZoneActive(null);

    try {
      const dragData = JSON.parse(e.dataTransfer.getData("text/plain"));
      let effectiveTargetQuarter = targetQuarter;
      
      // For years view: preserve the quarter number from source, only change the year
      if (planningTimeframe === 'years' && dragData.sourceQuarter) {
        const sourceMatch = dragData.sourceQuarter.match(/^(Q\d)/);
        const targetMatch = targetQuarter.match(/(\d{4})$/);
        if (sourceMatch && targetMatch) {
          effectiveTargetQuarter = `${sourceMatch[1]} ${targetMatch[1]}`;
        }
      }
      
      handleMoveRecommendation(
        dragData.recommendationId,
        dragData.sourceQuarter,
        effectiveTargetQuarter,
        targetMonth,
      );
    } catch (error) {
      // Silently handle drop errors
    }

    setDraggedItem(null);
  };

  // Touch event handlers for mobile
  const handleTouchStart = (
    e: React.TouchEvent,
    recommendation: any,
    sourceQuarter: string,
  ) => {
    if (!isMobile || recommendation.status !== "approved") return;
    
    const touch = e.touches[0];
    setTouchStartPos({ x: touch.clientX, y: touch.clientY });
    setTouchDraggedItem({ ...recommendation, sourceQuarter });
    setDraggedItem(recommendation);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchDraggedItem || !touchStartPos) return;
    
    const touch = e.touches[0];
    const deltaX = Math.abs(touch.clientX - touchStartPos.x);
    const deltaY = Math.abs(touch.clientY - touchStartPos.y);
    
    // If moved enough, prevent default scrolling
    if (deltaX > 10 || deltaY > 10) {
      e.preventDefault();
    }

    // Find element under touch point
    const elementBelow = document.elementFromPoint(touch.clientX, touch.clientY);
    const quarterColumn = elementBelow?.closest('[data-quarter]');
    
    if (quarterColumn) {
      const quarter = quarterColumn.getAttribute('data-quarter');
      if (quarter) {
        setDropZoneActive(quarter);
      }
    } else {
      setDropZoneActive(null);
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!touchDraggedItem || !touchStartPos) return;
    
    const touch = e.changedTouches[0];
    const elementBelow = document.elementFromPoint(touch.clientX, touch.clientY);
    const quarterColumn = elementBelow?.closest('[data-quarter]');
    
    if (quarterColumn) {
      let targetQuarter = quarterColumn.getAttribute('data-quarter');
      if (targetQuarter && targetQuarter !== touchDraggedItem.sourceQuarter) {
        // For years view: preserve the quarter number from source, only change the year
        if (planningTimeframe === 'years' && touchDraggedItem.sourceQuarter) {
          const sourceMatch = touchDraggedItem.sourceQuarter.match(/^(Q\d)/);
          const targetMatch = targetQuarter.match(/(\d{4})$/);
          if (sourceMatch && targetMatch) {
            targetQuarter = `${sourceMatch[1]} ${targetMatch[1]}`;
          }
        }
        
        handleMoveRecommendation(
          touchDraggedItem.id,
          touchDraggedItem.sourceQuarter,
          targetQuarter,
        );
      }
    }
    
    // Reset touch state
    setTouchStartPos(null);
    setTouchDraggedItem(null);
    setDraggedItem(null);
    setDropZoneActive(null);
  };

  const handleMoveRecommendation = (
    recommendationId: string | number,
    sourceQuarter: string,
    targetQuarter: string,
    targetMonth?: string,
  ) => {
    // Find the recommendation - check both regular recommendations and asset replacements
    let recommendation = allCustomerRecommendations.find((rec: any) => {
      const recId = rec.id.toString();
      const targetId = recommendationId.toString();
      return recId === targetId;
    });
    
    // Check if it's an asset replacement recommendation
    const assetReplacement = assetReplacements.find((rec: any) => {
      const recId = rec.id.toString();
      const targetId = recommendationId.toString();
      return recId === targetId;
    });
    
    if (assetReplacement) {
      // Handle asset replacement - update local state and add to QBR
      const sourceMonth = assetReplacement.targetMonth || null;
      const effectiveTargetMonth = targetMonth || null;
      
      if (sourceQuarter === targetQuarter && sourceMonth === effectiveTargetMonth) return;
      
      // Apply optimistic update immediately
      setOptimisticUpdates(prev => ({
        ...prev,
        [recommendationId]: { quarter: targetQuarter, month: targetMonth || null }
      }));
      
      // Update local asset replacement state
      setAssetReplacements(prev => prev.map(rec => {
        if (rec.id.toString() === recommendationId.toString()) {
          return { ...rec, targetQuarter, targetMonth: targetMonth || null };
        }
        return rec;
      }));
      
      // Also add the asset replacement to the current QBR's recommendations for persistence
      const currentRecommendations = currentQbr.recommendations || [];
      const updatedAssetRec = {
        ...assetReplacement,
        targetQuarter,
        targetMonth: targetMonth || null,
      };
      
      // Check if already exists in QBR recommendations
      const existsInQbr = currentRecommendations.some((rec: any) => rec.id === assetReplacement.id);
      const updatedRecommendations = existsInQbr
        ? currentRecommendations.map((rec: any) => 
            rec.id === assetReplacement.id 
              ? { ...rec, targetQuarter, targetMonth: targetMonth || null }
              : rec
          )
        : [...currentRecommendations, updatedAssetRec];
      
      // If adding a new recommendation (not updating existing), we need to adjust currentSlide
      // because recommendation slides appear before asset_lifecycle and budget_planning slides
      // Adding a new recommendation shifts all subsequent slides forward by 1
      // Only do this if recommendations slides are enabled in the presentation template
      if (!existsInQbr && presentationTemplate.slides.recommendations) {
        // Find the index of 'recommendations' in the slide order
        const recIndex = presentationTemplate.slideOrder.indexOf('recommendations');
        if (recIndex !== -1) {
          // Count how many slides come before recommendations
          let slidesBeforeRecs = 0;
          for (let i = 0; i < recIndex; i++) {
            const slideKey = presentationTemplate.slideOrder[i];
            if (presentationTemplate.slides[slideKey as keyof typeof presentationTemplate.slides]) {
              slidesBeforeRecs++;
            }
          }
          // Current recommendations count (before adding new one)
          const currentRecCount = currentRecommendations.length;
          // Slides up to and including all recommendations
          const slidesUpToEndOfRecs = slidesBeforeRecs + currentRecCount;
          
          // If current slide is after recommendations section, mark for adjustment
          // The actual adjustment will happen in useEffect when slides array updates
          if (currentSlide >= slidesUpToEndOfRecs) {
            pendingSlideAdjustmentRef.current += 1;
          }
        }
      }
      
      const updateData = { recommendations: updatedRecommendations };
      
      secureApiRequest(`/api/qbrs/${currentQbr.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updateData),
      })
        .then(() => {
          queryClient.invalidateQueries({ queryKey: ["/api/qbrs"] });
          queryClient.invalidateQueries({ queryKey: ["/api/qbrs", currentQbr.id] });
        })
        .catch(() => {
          setOptimisticUpdates(prev => {
            const newUpdates = { ...prev };
            delete newUpdates[recommendationId];
            return newUpdates;
          });
          toast({
            title: "Error",
            description: "Failed to assign replacement to quarter",
            variant: "destructive",
          });
        });
      
      return;
    }
    
    const sourceMonth = recommendation?.targetMonth || null;
    const effectiveTargetMonth = targetMonth || null;
    
    // Only skip if BOTH quarter AND month are the same (true no-op)
    if (sourceQuarter === targetQuarter && sourceMonth === effectiveTargetMonth) return;

    if (!recommendation) {
      return;
    }

    // Apply optimistic update immediately for instant visual feedback
    setOptimisticUpdates(prev => ({
      ...prev,
      [recommendationId]: { quarter: targetQuarter, month: targetMonth || null }
    }));

    const budgetAmount = parseFloat(recommendation.budgetAllocated) || 10000;

    // Get the QBR ID and index from the recommendation object
    const qbrId = recommendation.qbrId;
    const recIndex = recommendation.index;

    // Find the QBR that this recommendation belongs to
    const targetQbr = allQbrs.find((qbr: any) => qbr.id === qbrId);

    if (!targetQbr) {
      return;
    }

    // Update the specific QBR's recommendations
    const updatedRecommendations = (targetQbr.recommendations || []).map(
      (rec: any, index: number) => {
        if (index === recIndex) {
          return { ...rec, targetQuarter, targetMonth: targetMonth || null, budgetAllocated: budgetAmount };
        }
        return rec;
      },
    );

    // Update the specific QBR with new recommendations
    // Use secureApiRequest with the same format as customer-detail.tsx
    const updateData = { recommendations: updatedRecommendations };

    secureApiRequest(`/api/qbrs/${qbrId}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(updateData),
    })
      .then(async (response) => {
        // Invalidate multiple cache keys to ensure UI updates
        queryClient.invalidateQueries({ queryKey: ["/api/qbrs"] });
        queryClient.invalidateQueries({ queryKey: ["/api/qbr-recommendations"] });
        queryClient.invalidateQueries({ queryKey: ["/api/qbrs", qbrId] });
        queryClient.invalidateQueries({ queryKey: ["/api/qbrs", currentQbr.id] });
        // Toast removed - prevents blocking UI during presentation mode
      })
      .catch((error) => {
        // Revert optimistic update on error
        setOptimisticUpdates(prev => {
          const newUpdates = { ...prev };
          delete newUpdates[recommendationId];
          return newUpdates;
        });

        toast({
          title: "Update Failed",
          description: "Failed to save budget assignment.",
          variant: "destructive",
        });
      });
  };

  // Fetch organization data for logo
  const { data: organization } = useQuery<any>({
    queryKey: ["/api/organizations/current"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/organizations/current");
      return response;
    },
  });

  // Fetch customer assessments for alignment calculation
  const { data: customerAssessments = [] } = useQuery<any[]>({
    queryKey: ["/api/assessments", { customerId: customer?.id }],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/assessments?customerId=${customer?.id}`);
      return response;
    },
    enabled: !!customer?.id,
  });

  // Calculate overall alignment score
  const calculateAlignmentScore = () => {
    const approvedAssessments = customerAssessments.filter((a: any) => 
      a.status === 'approved' || a.status === 'completed'
    );

    if (approvedAssessments.length === 0) return 0;

    let totalQuestions = 0;
    let totalAlignedScore = 0;

    approvedAssessments.forEach((assessment: any) => {
      if (!assessment?.responses) return;

      const responses = assessment.responses;

      Object.entries(responses).forEach(([key, response]: [string, any]) => {
        // Skip note fields
        if (key.includes('_notes')) return;

        // Handle both string responses and object responses
        let responseValue = response;
        if (typeof response === 'object' && response?.rating) {
          responseValue = response.rating;
        }
        if (typeof response === 'object' && response?.value) {
          responseValue = response.value;
        }

        const responseString = String(responseValue).toLowerCase();

        totalQuestions++;

        if (responseString.includes('fully implemented') || responseString === 'fully_implemented') {
          totalAlignedScore += 1.0;
        } else if (responseString.includes('partially implemented') || responseString === 'partially_implemented') {
          totalAlignedScore += 0.5;
        }
      });
    });

    return totalQuestions === 0 ? 0 : Math.round((totalAlignedScore / totalQuestions) * 100);
  };

  // Get alignment score and color
  const getAlignmentBadge = (score: number) => {
    if (score >= 80) {
      return { color: "text-green-600 dark:text-green-400", bg: "bg-green-50 dark:bg-slate-950 border-green-300 dark:border-green-700", label: "Excellent", variant: "default" as const };
    } else if (score >= 60) {
      return { color: "text-orange-600 dark:text-orange-400", bg: "bg-orange-50 dark:bg-slate-950 border-orange-300 dark:border-orange-700", label: "Good", variant: "secondary" as const };
    } else {
      return { color: "text-red-600 dark:text-red-400", bg: "bg-red-50 dark:bg-slate-950 border-red-300 dark:border-red-700", label: "Needs Improvement", variant: "destructive" as const };
    }
  };

  const currentAlignmentScore = calculateAlignmentScore();
  const previousScore = Number(currentQbr.previousAlignmentScore) || 0;
  const scoreImprovement = currentAlignmentScore - previousScore;
  const alignmentBadge = getAlignmentBadge(currentAlignmentScore);

  // Get presentation template from local config, QBR config, customer template, or organization settings
  // Priority: Local (session) > QBR-specific > Customer default > Organization default > System default
  const presentationTemplate: PresentationTemplate = 
    localPresentationConfig ||
    currentQbr.presentationConfig || 
    customer?.presentationTemplate ||
    organization?.settings?.presentationTemplate || 
    DEFAULT_PRESENTATION_TEMPLATE;

  // Build slides array based on template configuration
  const buildSlides = () => {
    const slideDefinitions: Record<string, any> = {
      title: {
        type: "title",
        title: "Business Review",
        subtitle: customer?.name,
        organization,
      },
      executiveSummary: {
        type: "executive_summary",
        title: "Executive Summary",
        content: currentQbr.executiveSummary,
      },
      keyFindings: {
        type: "key_findings",
        title: "Key Findings",
        content: currentQbr.keyFindings,
      },
      serviceSummary: {
        type: "service_summary",
        title: "Service Summary",
        subtitle: "Ticket metrics and trends",
        customerId: customer?.id,
        psaCompanyId: psaCompanyId,
        customerName: customer?.name,
        reviewPeriod: currentQbr.reviewPeriod || currentQbr.quarter,
        recommendations: recommendations,
      },
      budgetPlanning: {
        type: "budget_planning",
        title: "Quarterly Budget Planning",
        subtitle: "Discuss and adjust budget allocations",
      },
      assetLifecycle: {
        type: "asset_lifecycle",
        title: "Asset Lifecycle Review",
        subtitle: "Hardware and software requiring attention",
      },
      implementationTracking: {
        type: "implementation_tracking",
        title: "Implementation Tracking",
        subtitle: "Status of all approved recommendations",
      },
      scheduleNextReview: { 
        type: "summary", 
        title: "Schedule Next Business Review" 
      },
      backupStatus: {
        type: "backup_status",
        title: "Backup Status",
        subtitle: "Backup health and device overview",
        customerId: customer?.id,
      },
      patchStatus: {
        type: "patch_status",
        title: "Patch Status",
        subtitle: "Patch approval and installation overview",
        customerId: customer?.id,
      },
    };

    const result: any[] = [];
    
    // Build slides in the configured order
    for (const slideKey of presentationTemplate.slideOrder) {
      // Check if this slide type is enabled
      if (!presentationTemplate.slides[slideKey as keyof SlideConfig]) {
        continue;
      }

      if (slideKey === "recommendations") {
        // Add individual recommendation slides
        recommendations.forEach((rec: any, index: number) => {
          result.push({
            type: "recommendation",
            title: `Recommendation ${index + 1}`,
            recommendation: rec,
            index,
          });
        });
      } else if (slideDefinitions[slideKey]) {
        result.push(slideDefinitions[slideKey]);
      }
    }

    return result;
  };

  const slides = buildSlides();
  
  // Track previous slides length to detect when new recommendations are added
  const prevSlidesLengthRef = useRef(slides.length);
  
  // Apply pending slide adjustments when the slides array changes
  useEffect(() => {
    if (pendingSlideAdjustmentRef.current > 0 && slides.length > prevSlidesLengthRef.current) {
      // New slides were added, apply the pending adjustment
      setCurrentSlide(prev => Math.min(prev + pendingSlideAdjustmentRef.current, slides.length - 1));
      pendingSlideAdjustmentRef.current = 0;
    }
    prevSlidesLengthRef.current = slides.length;
  }, [slides.length]);

  // Budget planning form
  const assignRecommendationSchema = z.object({
    targetQuarter: z.string().min(1, "Quarter is required"),
    budgetAllocated: z.string().min(1, "Budget allocation is required"),
    status: z
      .enum(["pending", "approved", "in_progress", "completed"])
      .optional(),
  });

  const assignRecommendationForm = useForm<
    z.infer<typeof assignRecommendationSchema>
  >({
    resolver: zodResolver(assignRecommendationSchema),
    defaultValues: {
      targetQuarter: "",
      budgetAllocated: "",
      status: "pending",
    },
  });

  // Budget planning mutations - Remove since we're not using the API
  const updateRecommendationMutation = useMutation({
    mutationFn: async (data: {
      recommendationId: number;
      [key: string]: any;
    }) => {
      // This is a placeholder - we'll update the QBR object directly
      return Promise.resolve(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/qbrs"] });
      // Toast removed - prevents blocking UI during presentation mode
    },
    onError: () => {
      toast({
        title: "Update Failed",
        description: "Failed to update recommendation budget.",
        variant: "destructive",
      });
    },
  });

  const updatePresentationMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await secureApiRequest(`/api/qbrs/${qbr.id}/presentation`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error("Failed to update presentation");
      return response.json();
    },
    onSuccess: () => {
      // Invalidate multiple cache keys to ensure UI updates
      queryClient.invalidateQueries({ queryKey: ["/api/qbrs"] });
      queryClient.invalidateQueries({ queryKey: ["/api/qbr-recommendations"] });
      queryClient.invalidateQueries({ queryKey: ["/api/qbrs", qbr.id] });
      // Removed toast notification to prevent unwanted notifications during presentation mode
    },
    onError: () => {
      toast({
        title: "Update Failed",
        description: "Failed to save presentation data.",
        variant: "destructive",
      });
    },
  });

  // Update yearly budget mutation
  const updateYearlyBudgetMutation = useMutation({
    mutationFn: async (data: { year: string; budget: number }) => {
      const yearlyBudgets = { ...(customer?.yearlyBudgets || {}), [data.year]: data.budget };
      
      const response = await secureApiRequest(`/api/customers/${customer.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ yearlyBudgets }),
      });

      if (!response.ok) {
        throw new Error("Failed to update yearly budget");
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/customers"] });
      queryClient.invalidateQueries({ queryKey: [`/api/customers/${customer.id}`] });
      setEditingYearBudget(null);
      toast({ 
        title: "Success", 
        description: "Yearly budget updated successfully" 
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update yearly budget",
        variant: "destructive",
      });
    },
  });

  const finalizePresentationMutation = useMutation({
    mutationFn: async () => {
      const approvalData = recommendations.map((rec: any, index: number) => ({
        recommendationIndex: index,
        recommendationTitle: rec.title,
        status: approvals[index] || "deferred",
        priority: rec.priority,
        estimatedCost: rec.estimatedCost,
        timeline: rec.timeline,
      }));

      // Combine date and time for next review using proper timezone handling
      let nextReviewDateTime = null;
      if (nextReviewDate) {
        nextReviewDateTime = createLocalDateTime(nextReviewDate, nextReviewTime || '10:00');
      }
      
      const response = await secureApiRequest(`/api/qbrs/${qbr.id}/presentation`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          status: "presented",
          customerApprovals: approvalData,
          meetingNotes,
          presentationDate: new Date().toISOString(),
          overallAlignmentScore: currentAlignmentScore,
          nextReviewScheduledDate: nextReviewDateTime,
          skipNextReviewCreation: true,
        }),
      });
      
      if (!response.ok) {
        throw new Error("Failed to finalize presentation");
      }
      
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/qbrs"] });
      toast({
        title: "Presentation Complete",
        description:
          "QBR presentation has been finalized with customer feedback.",
      });
      setShowNextReviewConfirmation(true);
    },
    onError: () => {
      toast({
        title: "Finalization Failed",
        description: "Failed to finalize presentation.",
        variant: "destructive",
      });
    },
  });
  
  // Handle creating next review after user confirmation
  const handleCreateNextReview = async () => {
    setIsCreatingNextReview(true);
    try {
      let nextReviewDateTime = null;
      if (nextReviewDate) {
        nextReviewDateTime = createLocalDateTime(nextReviewDate, nextReviewTime || '10:00');
      }
      
      const response = await secureApiRequest(`/api/qbrs/${qbr.id}/create-next-review`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          scheduledDate: nextReviewDateTime,
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to create next review");
      }
      
      queryClient.invalidateQueries({ queryKey: ["/api/qbrs"] });
      queryClient.invalidateQueries({ queryKey: ["/api/business-reviews/upcoming"] });
      toast({
        title: "Next Review Created",
        description: `The next Business Review for ${customer?.name} has been scheduled.`,
      });
    } catch (error: any) {
      toast({
        title: "Failed to Create Next Review",
        description: error.message || "An error occurred while creating the next review.",
        variant: "destructive",
      });
    } finally {
      setIsCreatingNextReview(false);
      setShowNextReviewConfirmation(false);
      onClose();
    }
  };
  
  const handleSkipNextReview = () => {
    setShowNextReviewConfirmation(false);
    onClose();
  };

  const handleApprovalChange = (
    recIndex: number,
    status: "approved" | "rejected" | "deferred",
  ) => {
    setApprovals((prev) => ({ ...prev, [recIndex]: status }));
  };

  // Budget planning functions already defined above

  const onAssignRecommendation = (
    data: z.infer<typeof assignRecommendationSchema>,
  ) => {
    if (!assigningRecommendation) return;

    updateRecommendationMutation.mutate({
      recommendationId: assigningRecommendation.id,
      targetQuarter: data.targetQuarter,
      budgetAllocated: parseFloat(data.budgetAllocated),
      status: data.status || "pending",
    });

    setAssigningRecommendation(null);
    assignRecommendationForm.reset();
  };

  const saveProgress = () => {
    const approvalData = recommendations.map((rec: any, index: number) => ({
      recommendationIndex: index,
      recommendationTitle: rec.title,
      status: approvals[index] || "deferred",
      priority: rec.priority,
      estimatedCost: rec.estimatedCost,
      timeline: rec.timeline,
    }));

    updatePresentationMutation.mutate({
      customerApprovals: approvalData,
      meetingNotes,
    });
  };

  // Navigate to slide without auto-save to prevent unwanted toast notifications during presentation
  const navigateToSlide = (newSlideIndex: number) => {
    setCurrentSlide(newSlideIndex);
  };

  // Keyboard navigation: left/right arrow keys
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") {
        // Navigate to previous slide
        if (currentSlide > 0) {
          navigateToSlide(currentSlide - 1);
        }
      } else if (event.key === "ArrowRight") {
        // Navigate to next slide
        if (currentSlide < slides.length - 1) {
          navigateToSlide(currentSlide + 1);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [currentSlide, slides.length]);


  const renderSlide = () => {
    const slide = slides[currentSlide];
    
    if (!slide) {
      return (
        <div className="flex items-center justify-center h-full">
          <p className="text-muted-foreground">Loading slide...</p>
        </div>
      );
    }

    switch (slide.type) {
      case "title":
        return (
          <div className="text-center space-y-8 py-16">
            {/* Organization Logo */}
            {slide.organization?.logo && (
              <div className="flex justify-center mb-8">
                <img
                  src={slide.organization.logo}
                  alt={slide.organization.brandName || slide.organization.name}
                  className="h-20 w-auto max-w-xs object-contain"
                />
              </div>
            )}

            <div className="space-y-3">
              <h1 className="text-5xl font-bold text-foreground">
                Business Review
              </h1>
              <div className="space-y-4">
                <p className="text-lg text-muted-foreground">
                  <span className="text-lg">Prepared for</span>
                </p>
                {/* Customer Logo */}
                {customer?.logoUrl && (
                  <div className="flex justify-center">
                    <img
                      src={customer.logoUrl}
                      alt={`${customer.name} logo`}
                      className="h-16 w-auto max-w-xs object-contain"
                    />
                  </div>
                )}
                <p className="font-semibold text-xl text-foreground">{customer?.name}</p>
              </div>
              <p className="text-lg text-muted-foreground">
                {qbr.reviewPeriod || qbr.quarter} • {new Date().toLocaleDateString()}
              </p>
            </div>

            {/* Organization Branding */}
            <div className="flex justify-center">
              <Badge variant="outline" className="text-lg px-3 py-1.5">
                <Users className="h-5 w-5 mr-2" />
                Confidential Business Review
              </Badge>
            </div>

            {/* MSP Company Name */}
            {slide.organization && (
              <div className="text-center mt-8 pt-8 border-t border-border">
                <p className="text-lg text-muted-foreground mb-4">
                  Presented by{" "}
                  <span className="font-semibold text-foreground">
                    {slide.organization.settings?.brandName || slide.organization.name}
                  </span>
                </p>
                {/* Organization Logo below Presented by */}
                <div className="flex justify-center">
                  <img
                    src={slide.organization?.settings?.logo || defaultLogo}
                    alt={
                      slide.organization?.settings?.brandName || slide.organization?.name || "TechAlign"
                    }
                    className="h-32 w-auto max-w-md object-contain"
                  />
                </div>
              </div>
            )}
          </div>
        );

      case "executive_summary":
        return (
          <div className="space-y-8">
            <h2 className="text-4xl font-bold text-center mb-8">
              Executive Summary
            </h2>

            {/* Alignment Score Dashboard */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <Card className="text-center p-4">
                <CardContent className="space-y-3">
                  <Target className={`h-8 w-8 mx-auto ${alignmentBadge.color}`} />
                  <div>
                    <div className="text-3xl font-bold text-foreground">
                      {currentAlignmentScore}%
                    </div>
                    <p className="text-sm text-muted-foreground">Current Alignment Score</p>
                  </div>
                  <Badge variant={alignmentBadge.variant} className="text-sm">
                    {alignmentBadge.label}
                  </Badge>
                </CardContent>
              </Card>

              <Card className="text-center p-4">
                <CardContent className="space-y-3">
                  <Activity className="h-8 w-8 mx-auto text-blue-600" />
                  <div>
                    <div className="text-3xl font-bold text-foreground">
                      {previousScore}%
                    </div>
                    <p className="text-sm text-muted-foreground">Previous Alignment Score</p>
                  </div>
                  <Badge variant="outline" className="text-sm">
                    Baseline
                  </Badge>
                </CardContent>
              </Card>

              <Card className="text-center p-4">
                <CardContent className="space-y-3">
                  {scoreImprovement > 0 ? (
                    <ChevronRight className="h-8 w-8 mx-auto text-green-600 transform rotate-90" />
                  ) : scoreImprovement < 0 ? (
                    <ChevronRight className="h-8 w-8 mx-auto text-red-600 transform -rotate-90" />
                  ) : (
                    <ChevronRight className="h-8 w-8 mx-auto text-muted-foreground transform rotate-0" />
                  )}
                  <div>
                    <div className={`text-3xl font-bold ${scoreImprovement > 0 ? 'text-green-600' : scoreImprovement < 0 ? 'text-red-600' : 'text-muted-foreground'}`}>
                      {scoreImprovement > 0 ? '+' : ''}{scoreImprovement}%
                    </div>
                    <p className="text-sm text-muted-foreground">Improvement</p>
                  </div>
                  <Badge 
                    variant={scoreImprovement > 0 ? "default" : scoreImprovement < 0 ? "destructive" : "outline"} 
                    className="text-sm"
                  >
                    {scoreImprovement > 0 ? 'Improving' : scoreImprovement < 0 ? 'Declining' : 'No Change'}
                  </Badge>
                </CardContent>
              </Card>
            </div>

            <Card className="p-8">
              <CardContent className="space-y-3">
                <div className="text-lg leading-relaxed text-foreground whitespace-pre-line">
                  {decodeHtmlEntities(slide.content) ||
                    "Executive summary content will be displayed here."}
                </div>

                {/* Additional alignment context */}
                {currentAlignmentScore > 0 && (
                  <div className={`p-4 rounded-lg ${alignmentBadge.bg} border`}>
                    <div className="flex items-center space-x-3">
                      <Target className={`h-5 w-5 ${alignmentBadge.color}`} />
                      <div>
                        <p className={`font-medium ${alignmentBadge.color}`}>
                          Overall Technical Alignment: {alignmentBadge.label}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {currentAlignmentScore >= 80 
                            ? "Your technical infrastructure demonstrates excellent alignment with industry best practices."
                            : currentAlignmentScore >= 60
                            ? "Your technical infrastructure shows good alignment with opportunities for strategic improvements."
                            : "Your technical infrastructure has significant opportunities for alignment improvements to enhance security and efficiency."
                          }
                          {scoreImprovement > 0 && ` This represents a ${scoreImprovement}% improvement since the last QBR.`}
                          {scoreImprovement < 0 && ` This represents a ${Math.abs(scoreImprovement)}% decline since the last QBR that requires attention.`}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        );

      case "key_findings":
        return (
          <div className="space-y-8">
            <h2 className="text-4xl font-bold text-center mb-8">
              Key Findings
            </h2>
            <div className="grid gap-4">
              {(slide.content || []).map((finding: any, index: number) => (
                <Card key={index} className="p-4">
                  <CardContent className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <Target className="h-6 w-6 text-blue-600" />
                    </div>
                    <div className="space-y-2">
                      <h3 className="text-lg font-semibold text-foreground">{finding.title}</h3>
                      <p className="text-muted-foreground">
                        {finding.description}
                      </p>
                      <Badge
                        variant={
                          (finding.severity || finding.impact) === "critical" || (finding.severity || finding.impact) === "high"
                            ? "destructive"
                            : (finding.severity || finding.impact) === "medium"
                              ? "default"
                              : "secondary"
                        }
                      >
                        {(finding.severity || finding.impact || "medium").charAt(0).toUpperCase() + (finding.severity || finding.impact || "medium").slice(1)}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        );

      case "service_summary":
        return (
          <QbrTicketSummarySlide
            psaCompanyId={slide.psaCompanyId}
            customerName={slide.customerName}
            reviewPeriod={slide.reviewPeriod}
            recommendations={slide.recommendations}
          />
        );

      case "budget_planning":
        const quarterlyBudgets = getQuarterlyBudgets();
        const annualBreakdown = getAnnualBudgetBreakdown();
        const planningBoardData = getPlanningBoardData();
        const hasUnassignedRecommendations = unassignedRecommendations.length > 0;

        return (
          <div className="space-y-3 h-full flex flex-col">
            {/* Header with View Toggle - Centered */}
            <div className="text-center space-y-2">
              <h2 className="text-3xl font-bold text-foreground">Budget Planning Board</h2>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-2">
                <p className="text-lg text-muted-foreground">
                  Drag recommendations between {planningTimeframe === 'months' ? 'months' : planningTimeframe === 'years' ? 'years' : 'quarters'} to adjust budget allocations
                </p>
                
                {/* Timeframe Selector */}
                <div className="flex items-center gap-2 justify-center">
                  <span className="text-xs text-muted-foreground hidden sm:inline">View by:</span>
                  <div className="flex rounded-lg border bg-muted/30 p-1">
                    <button
                      onClick={() => setPlanningTimeframe("months")}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                        planningTimeframe === "months"
                          ? "bg-primary text-primary-foreground shadow-sm"
                          : "text-muted-foreground hover:text-foreground hover:bg-muted"
                      }`}
                    >
                      Months
                    </button>
                    <button
                      onClick={() => setPlanningTimeframe("quarters")}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                        planningTimeframe === "quarters"
                          ? "bg-primary text-primary-foreground shadow-sm"
                          : "text-muted-foreground hover:text-foreground hover:bg-muted"
                      }`}
                    >
                      Quarters
                    </button>
                    <button
                      onClick={() => setPlanningTimeframe("years")}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                        planningTimeframe === "years"
                          ? "bg-primary text-primary-foreground shadow-sm"
                          : "text-muted-foreground hover:text-foreground hover:bg-muted"
                      }`}
                    >
                      Years
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Annual Budget Overview */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              {annualBreakdown.map((year: any) => {
                const yearlyBudget = customer?.yearlyBudgets?.[year.year] || 0;
                const utilization = yearlyBudget > 0 ? Math.round((year.allocated / yearlyBudget) * 100) : 0;
                const remaining = yearlyBudget - year.allocated;
                const isOverBudget = yearlyBudget > 0 && year.allocated > yearlyBudget;

                return (
                  <div
                    key={year.year}
                    className={`p-3 border rounded-lg ${
                      isOverBudget 
                        ? 'border-red-500 bg-red-50/50 dark:bg-red-950/20' 
                        : 'border-border'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-sm">{year.year}</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 w-6 p-0"
                        onClick={() => setEditingYearBudget({ year: year.year, budget: yearlyBudget })}
                      >
                        <Settings2 className="h-3 w-3" />
                      </Button>
                    </div>
                    {yearlyBudget > 0 ? (
                      <>
                        <div className="grid grid-cols-3 gap-2 text-center mb-2">
                          <div>
                            <div className="text-xs font-bold">${(yearlyBudget / 1000).toFixed(0)}K</div>
                            <div className="text-[10px] text-muted-foreground">Budget</div>
                          </div>
                          <div>
                            <div className="text-xs font-bold text-blue-600">${(year.allocated / 1000).toFixed(0)}K</div>
                            <div className="text-[10px] text-muted-foreground">Allocated</div>
                          </div>
                          <div>
                            <div className={`text-xs font-bold ${remaining >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              ${Math.abs(remaining / 1000).toFixed(0)}K
                            </div>
                            <div className="text-[10px] text-muted-foreground">{remaining >= 0 ? 'Remaining' : 'Over'}</div>
                          </div>
                        </div>
                        <div className="w-full bg-muted rounded-full h-1.5">
                          <div
                            className={`h-1.5 rounded-full ${isOverBudget ? "bg-red-500" : "bg-green-500"}`}
                            style={{ width: `${Math.min(100, utilization)}%` }}
                          />
                        </div>
                        <div className="text-[10px] text-muted-foreground text-center mt-1">
                          {utilization}% allocated
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-2">
                        <Button
                          size="sm"
                          variant="default"
                          onClick={() => setEditingYearBudget({ year: year.year, budget: 0 })}
                          className="text-xs"
                        >
                          Set Budget
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Navigation - only show for quarters view */}
            {planningTimeframe === 'quarters' && (
              <div className="flex items-center justify-between">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={navigatePrevious}
                  disabled={!canNavigatePrevious()}
                  className="flex items-center space-x-1"
                >
                  <ChevronLeft className="h-4 w-4" />
                  <span>Previous</span>
                </Button>

                <div className="text-center">
                  <p className="text-sm text-muted-foreground">
                    {getVisibleQuarters()[0]} - {getVisibleQuarters()[getVisibleQuarters().length - 1]}
                  </p>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={navigateNext}
                  disabled={!canNavigateNext()}
                  className="flex items-center space-x-1"
                >
                  <span>Next</span>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}

            {/* Budget Planning Board - Responsive grid based on timeframe */}
            {/* Expands to fill available space when no unassigned recommendations */}
            <div className={`grid gap-3 overflow-y-auto pr-2 flex-1 ${
              hasUnassignedRecommendations ? 'max-h-[500px]' : ''
            } ${
              planningTimeframe === 'years' 
                ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' 
                : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
            }`}>
              {(planningTimeframe === 'quarters' 
                ? planningBoardData.filter((data: any) => getVisibleQuarters().includes(data.quarter))
                : planningBoardData
              ).map((periodData: any, index: number) => {
                const displayLabel = periodData.displayLabel || periodData.quarter || periodData.period;
                const dragDropQuarter = periodData.dragDropQuarter;
                const targetMonth = periodData.targetMonth; // Specific month for month-level assignment
                const quarter = dragDropQuarter || displayLabel;
                const uniqueKey = `${displayLabel}-${index}`;
                const quarterRecommendations = periodData?.recommendations || [];
                const quarterExpenses = periodData?.expenses || [];
                const quarterTotal = periodData?.allocated || 0;
                const totalItems = quarterRecommendations.length + quarterExpenses.length;
                
                // For monthly view, check if this is the first month of a quarter
                const isFirstMonthOfQuarter = periodData.isFirstMonthOfQuarter !== false;
                // Enable drag and drop for all timeframes that have a dragDropQuarter set
                const canDragDrop = !!dragDropQuarter;
                // Use displayLabel as drop zone identifier for months view to allow each month to be a unique drop target
                const dropZoneId = planningTimeframe === 'months' ? displayLabel : dragDropQuarter;

                return (
                  <div
                    key={uniqueKey}
                    data-quarter={dragDropQuarter}
                    className={`border rounded-lg transition-all duration-200 shadow-sm hover:shadow-md ${
                      canDragDrop && dropZoneActive === dropZoneId
                        ? "ring-2 ring-blue-500 ring-offset-2 bg-blue-50/50 dark:bg-blue-950/20 scale-[1.02]"
                        : ""
                    } ${
                      canDragDrop && draggedItem
                        ? "hover:ring-2 hover:ring-green-500 hover:ring-offset-2"
                        : ""
                    }`}
                    onDragOver={canDragDrop ? handleDragOver : undefined}
                    onDragEnter={canDragDrop ? (e) => handleDragEnter(e, dropZoneId) : undefined}
                    onDragLeave={canDragDrop ? handleDragLeave : undefined}
                    onDrop={canDragDrop ? (e) => handleDrop(e, dragDropQuarter, targetMonth) : undefined}
                  >
                    {/* Enhanced Period Header */}
                    <div className="bg-gradient-to-r from-muted/30 to-muted/50 border-b p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <Calendar className="h-4 w-4 text-primary" />
                          <h3 className="font-semibold text-base text-foreground">
                            {displayLabel}
                          </h3>
                          {planningTimeframe === 'months' && !isFirstMonthOfQuarter && (
                            <span className="text-[10px] text-muted-foreground">({dragDropQuarter})</span>
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="text-xs bg-primary/10 text-primary px-2 py-1 rounded flex items-center space-x-1">
                            <Move className="h-3 w-3" />
                            <span>{totalItems}</span>
                          </div>
                          {canDragDrop && dropZoneActive === dropZoneId && (
                            <div className="animate-pulse text-xs bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-100 px-2 py-1 rounded">
                              Drop here
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Enhanced Budget Display */}
                      <div className="text-sm space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground flex items-center space-x-1">
                            <DollarSign className="h-3 w-3" />
                            <span>Budget:</span>
                          </span>
                          <span className="font-bold text-lg">
                            ${quarterTotal.toLocaleString()}
                          </span>
                        </div>

                        {/* Projects vs Expenses Breakdown */}
                        {((periodData.projectsAllocated || 0) > 0 || (periodData.expensesAllocated || 0) > 0) && (
                          <div className="space-y-1 text-xs">
                            <div className="flex justify-between text-muted-foreground">
                              <span>Projects:</span>
                              <span className="font-medium text-foreground">
                                ${(periodData.projectsAllocated || 0).toLocaleString()}
                              </span>
                            </div>
                            <div className="flex justify-between text-muted-foreground">
                              <span>IT Expenses:</span>
                              <span className="font-medium text-foreground">
                                ${(periodData.expensesAllocated || 0).toLocaleString()}
                              </span>
                            </div>
                          </div>
                        )}

                        {/* Visual Budget Progress */}
                        {periodData && periodData.allocated > 0 && (
                          <div className="space-y-1">
                            <div className="flex justify-between text-xs text-muted-foreground">
                              <span>Utilization</span>
                              <span>
                                {Math.round((periodData.actual / periodData.allocated) * 100)}%
                              </span>
                            </div>
                            <div className="w-full bg-muted rounded-full h-2">
                              <div
                                className={`h-2 rounded-full transition-all ${
                                  periodData.actual <= periodData.allocated
                                    ? "bg-green-500"
                                    : "bg-red-500"
                                }`}
                                style={{
                                  width: `${Math.min(100, (periodData.actual / periodData.allocated) * 100)}%`,
                                }}
                              />
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-muted-foreground">
                                Remaining:
                              </span>
                              <span
                                className={`font-medium ${
                                  periodData.remaining >= 0 ? "text-green-600" : "text-red-600"
                                }`}
                              >
                                ${Math.abs(periodData.remaining).toLocaleString()}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Empty Month Drop Zone - for months with no projects assigned yet */}
                    {planningTimeframe === 'months' && quarterRecommendations.length === 0 && quarterExpenses.length === 0 && (
                      <div className="p-3">
                        <div className={`text-center py-6 border-2 border-dashed rounded-lg transition-colors ${
                          dropZoneActive === dropZoneId 
                            ? 'border-blue-500 bg-blue-50/50 dark:bg-blue-950/20' 
                            : 'border-muted hover:border-primary/50'
                        }`}>
                          <Calendar className="mx-auto h-8 w-8 text-muted-foreground/50 mb-3" />
                          <p className="text-xs text-muted-foreground">
                            Drop projects here for {displayLabel}
                          </p>
                          <p className="text-[10px] text-muted-foreground mt-1">
                            Part of {dragDropQuarter}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Period Content with Two Sections: PROJECTS and IT EXPENSES */}
                    {(planningTimeframe !== 'months' || quarterRecommendations.length > 0 || quarterExpenses.length > 0) && (
                    <div className="p-3 min-h-[300px] max-h-[450px] overflow-y-auto space-y-3">
                      {/* PROJECTS SECTION */}
                      <div className="space-y-2">
                        {/* Projects Section Header */}
                        <button
                          onClick={() => toggleSection(quarter, 'projects')}
                          className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            {isSectionCollapsed(quarter, 'projects') ? (
                              <ChevronRight className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <ChevronDown className="h-4 w-4 text-muted-foreground" />
                            )}
                            <Briefcase className="h-4 w-4 text-primary" />
                            <span className="font-medium text-sm">PROJECTS</span>
                            <Badge variant="secondary" className="text-xs">
                              {quarterRecommendations.length}
                            </Badge>
                          </div>
                          <span className="text-xs text-muted-foreground">
                            ${(periodData.projectsAllocated || 0).toLocaleString()}
                          </span>
                        </button>

                        {/* Projects Content */}
                        {!isSectionCollapsed(quarter, 'projects') && (
                          <div className="space-y-2">
                            {quarterRecommendations.length > 0 ? (
                              quarterRecommendations.map((rec: any) => (
                          <div
                            key={rec.id}
                            className={`bg-card border rounded-lg p-3 transition-all duration-200 touch-none ${
                              draggedItem?.id === rec.id
                                ? "opacity-50 scale-95 rotate-2"
                                : rec.status === "approved"
                                  ? "hover:shadow-lg cursor-move active:scale-95"
                                  : "cursor-default"
                            } ${
                              draggedItem && draggedItem.id !== rec.id
                                ? "opacity-75"
                                : ""
                            } ${rec.status !== "approved" ? "opacity-60" : ""}`}
                            draggable={rec.status === "approved" && !isMobile}
                            onDragStart={(e) => {
                              if (rec.status !== "approved") {
                                e.preventDefault();
                                return;
                              }
                              handleDragStart(e, rec, quarter);
                            }}
                            onDragEnd={() => setDraggedItem(null)}
                            onTouchStart={(e) => handleTouchStart(e, rec, quarter)}
                            onTouchMove={handleTouchMove}
                            onTouchEnd={handleTouchEnd}
                          >
                            <div className="space-y-2">
                              {/* Header with drag indicator */}
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex items-start space-x-2 flex-1 min-w-0">
                                  <Move className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                                  <h4 className="font-medium leading-tight text-sm break-words">
                                    {decodeHtmlEntities(rec.title)}
                                  </h4>
                                </div>
                              </div>

                              <p className="text-muted-foreground line-clamp-2 text-xs">
                                {rec.description}
                              </p>

                              {/* Enhanced Budget Controls */}
                              <div className="space-y-1">
                                <div className="flex items-center justify-between gap-2">
                                  <div className="flex items-center space-x-1">
                                    <DollarSign className="h-3 w-3 text-muted-foreground" />
                                    <span className="font-medium text-sm">
                                      ${rec.budgetAllocated?.toLocaleString() || "TBD"}
                                    </span>
                                  </div>
                                  <Badge
                                    variant={
                                      rec.status === "completed"
                                        ? "default"
                                        : rec.status === "in_progress"
                                          ? "secondary"
                                          : rec.status === "approved"
                                            ? "outline"
                                            : "secondary"
                                    }
                                    className="text-xs"
                                  >
                                    {rec.status === "pending" && <Clock className="mr-1 h-2 w-2" />}
                                    {rec.status === "approved" && <CheckCircle className="mr-1 h-2 w-2" />}
                                    {rec.status === "in_progress" && <Activity className="mr-1 h-2 w-2" />}
                                    {rec.status === "completed" && <CheckCircle className="mr-1 h-2 w-2" />}
                                    {rec.status}
                                  </Badge>
                                </div>

                                {/* Actual Cost Display */}
                                {rec.actualCost && parseFloat(rec.actualCost) > 0 && (
                                  <div className="text-xs text-muted-foreground">
                                    Spent: ${parseFloat(rec.actualCost).toLocaleString()}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                              ))
                            ) : (
                              <div
                                className={`text-center py-4 border-2 border-dashed rounded-lg transition-all duration-200 ${
                                  dropZoneActive === quarter
                                    ? "border-green-500 bg-green-50/50 dark:bg-green-950/20"
                                    : "border-muted"
                                }`}
                              >
                                <DollarSign className="mx-auto h-6 w-6 text-muted-foreground mb-2" />
                                <p className="text-xs text-muted-foreground px-2">
                                  {dropZoneActive === quarter
                                    ? "Drop here!"
                                    : "Drop approved recommendations here"}
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* IT EXPENSES SECTION */}
                      <div className="space-y-2">
                        {/* IT Expenses Section Header */}
                        <button
                          onClick={() => toggleSection(quarter, 'expenses')}
                          className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            {isSectionCollapsed(quarter, 'expenses') ? (
                              <ChevronRight className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <ChevronDown className="h-4 w-4 text-muted-foreground" />
                            )}
                            <Receipt className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                            <span className="font-medium text-sm">IT EXPENSES</span>
                            <Badge variant="outline" className="text-xs bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800">
                              {quarterExpenses.length}
                            </Badge>
                          </div>
                          <span className="text-xs text-muted-foreground">
                            ${(periodData.expensesAllocated || 0).toLocaleString()}
                          </span>
                        </button>

                        {/* IT Expenses Content */}
                        {!isSectionCollapsed(quarter, 'expenses') && (
                          <div className="space-y-2">
                            {quarterExpenses.length > 0 ? (
                              quarterExpenses.map((expense: any) => (
                                <div
                                  key={expense.id}
                                  className="bg-blue-50/50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-2"
                                >
                                  <div className="space-y-1">
                                    <div className="flex items-start justify-between gap-2">
                                      <h4 className="font-medium text-xs leading-tight">
                                        {expense.name}
                                      </h4>
                                      <Badge variant="outline" className="text-xs">
                                        {expense.frequency}
                                      </Badge>
                                    </div>
                                    {expense.vendor && (
                                      <p className="text-xs text-muted-foreground">
                                        {expense.vendor}
                                      </p>
                                    )}
                                    <div className="flex items-center justify-between">
                                      <span className="text-xs text-muted-foreground">
                                        {formatCategoryName(expense.category)}
                                      </span>
                                      <span className="text-xs font-medium">
                                        ${(expense.monthlyAmount !== undefined 
                                          ? expense.monthlyAmount 
                                          : expense.yearlyAmount !== undefined 
                                            ? expense.yearlyAmount 
                                            : getExpenseAmountForQuarter(expense)
                                        ).toLocaleString()}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="text-center py-4 border-2 border-dashed rounded-lg border-muted">
                                <Receipt className="mx-auto h-6 w-6 text-muted-foreground mb-2" />
                                <p className="text-xs text-muted-foreground px-2">
                                  No IT expenses for this {planningTimeframe === 'months' ? 'month' : planningTimeframe === 'years' ? 'year' : 'quarter'}
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Unassigned Recommendations */}
            {unassignedRecommendations.length > 0 && (
              <Card className="shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="flex flex-wrap items-center gap-2 text-base">
                    <Target className="h-5 w-5" />
                    <span>Unassigned Recommendations</span>
                    <Badge variant="secondary" className="text-xs">
                      {unassignedRecommendations.length}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                    {unassignedRecommendations.map((rec: any) => (
                      <div
                        key={rec.id}
                        className={`bg-muted/50 border-2 border-dashed border-muted-foreground/20 rounded-lg p-3 transition-all duration-200 cursor-move touch-none ${
                          draggedItem?.id === rec.id
                            ? "opacity-50 scale-95"
                            : "hover:border-primary/50 hover:bg-primary/5 active:scale-95"
                        }`}
                        draggable={true}
                        onDragStart={(e) => handleDragStart(e, rec, "unassigned")}
                        onDragEnd={() => setDraggedItem(null)}
                        onTouchStart={(e) => handleTouchStart(e, rec, "unassigned")}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                      >
                        <div className="space-y-2">
                          <div className="flex items-start space-x-2">
                            <Move className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                            <h4 className="font-medium text-sm leading-tight">
                              {decodeHtmlEntities(rec.title)}
                            </h4>
                          </div>
                          <p className="text-xs text-muted-foreground line-clamp-2">
                            {rec.description}
                          </p>
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-muted-foreground">
                              Drag to quarter
                            </span>
                            <Badge variant="outline" className="text-xs">
                              {rec.budgetAllocated ? `$${rec.budgetAllocated.toLocaleString()}` : "TBD"}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        );

      case "recommendation":
        const rec = slide.recommendation;
        const recIndex = slide.index;
        const currentApproval = approvals[recIndex];

        return (
          <div className="space-y-8">
            <h2 className="text-4xl font-bold text-center mb-8">
              Recommendation {recIndex + 1}
            </h2>
            <Card className="p-8">
              <CardHeader>
                <CardTitle className="text-lg">{rec.title}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-lg text-foreground">
                  {rec.description}
                </p>

                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="flex items-center justify-center mb-2">
                      <Badge className="h-6 w-6 mr-2" />
                      <span className="font-semibold text-foreground">Priority</span>
                    </div>
                    <Badge
                      variant={
                        rec.priority === "high"
                          ? "destructive"
                          : rec.priority === "medium"
                            ? "default"
                            : "secondary"
                      }
                    >
                      {rec.priority.charAt(0).toUpperCase() + rec.priority.slice(1)}
                    </Badge>
                  </div>

                  <div className="text-center">
                    <div className="flex items-center justify-center mb-2">
                      <Clock className="h-6 w-6 mr-2" />
                      <span className="font-semibold text-foreground">Timeline</span>
                    </div>
                    <p className="text-muted-foreground">
                      {rec.timeline}
                    </p>
                  </div>

                  <div className="text-center">
                    <div className="flex items-center justify-center mb-2">
                      <DollarSign className="h-6 w-6 mr-2" />
                      <span className="font-semibold text-foreground">Investment</span>
                    </div>
                    <p className="text-muted-foreground">
                      {rec.estimatedCost}
                    </p>
                  </div>
                </div>

                {/* Customer Decision Section */}
                <div className="mt-8 p-4 bg-muted/50 rounded-lg border">
                  <h4 className="text-lg font-semibold mb-4 text-foreground">
                    Customer Decision:
                  </h4>
                  <div className="flex space-x-3">
                    <Button
                      variant={
                        currentApproval === "approved" ? "default" : "outline"
                      }
                      onClick={() => handleApprovalChange(recIndex, "approved")}
                      className={`flex items-center space-x-2 ${
                        currentApproval === "approved" 
                          ? "bg-green-600 hover:bg-green-700 text-white" 
                          : "hover:bg-green-50 hover:text-green-700 hover:border-green-300 dark:hover:bg-green-950 dark:hover:text-green-400"
                      }`}
                    >
                      <CheckCircle className="h-4 w-4" />
                      <span>Approved</span>
                    </Button>
                    <Button
                      variant={
                        currentApproval === "rejected"
                          ? "destructive"
                          : "outline"
                      }
                      onClick={() => handleApprovalChange(recIndex, "rejected")}
                      className={`flex items-center space-x-2 ${
                        currentApproval === "rejected" 
                          ? "" 
                          : "hover:bg-red-50 hover:text-red-700 hover:border-red-300 dark:hover:bg-red-950 dark:hover:text-red-400"
                      }`}
                    >
                      <XCircle className="h-4 w-4" />
                      <span>Rejected</span>
                    </Button>
                    <Button
                      variant={
                        currentApproval === "deferred" ? "secondary" : "outline"
                      }
                      onClick={() => handleApprovalChange(recIndex, "deferred")}
                      className={`flex items-center space-x-2 ${
                        currentApproval === "deferred" 
                          ? "" 
                          : "hover:bg-yellow-50 hover:text-yellow-700 hover:border-yellow-300 dark:hover:bg-yellow-950 dark:hover:text-yellow-400"
                      }`}
                    >
                      <Clock className="h-4 w-4" />
                      <span>Deferred</span>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case "asset_lifecycle":
        return (
          <div className="space-y-4">
            {/* Header */}
            <div className="text-center space-y-2">
              <h2 className="text-3xl font-bold text-foreground">Asset Lifecycle Review</h2>
              <p className="text-lg text-muted-foreground">
                Hardware and software assets requiring attention
              </p>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="text-center p-4">
                <CardContent className="space-y-2 pt-2">
                  <HardDrive className="h-8 w-8 mx-auto text-blue-600" />
                  <div className="text-3xl font-bold text-foreground">
                    {assetLifecycleMetrics.total}
                  </div>
                  <p className="text-sm text-muted-foreground">Total Assets</p>
                </CardContent>
              </Card>

              <Card className="text-center p-4 border-orange-200 dark:border-orange-800">
                <CardContent className="space-y-2 pt-2">
                  <Shield className="h-8 w-8 mx-auto text-orange-500" />
                  <div className="text-3xl font-bold text-orange-600">
                    {assetLifecycleMetrics.warrantyExpiring.length}
                  </div>
                  <p className="text-sm text-muted-foreground">Warranties Expiring (90 days)</p>
                </CardContent>
              </Card>

              <Card className="text-center p-4 border-red-200 dark:border-red-800">
                <CardContent className="space-y-2 pt-2">
                  <AlertTriangle className="h-8 w-8 mx-auto text-red-500" />
                  <div className="text-3xl font-bold text-red-600">
                    {assetLifecycleMetrics.agingAssets.length}
                  </div>
                  <p className="text-sm text-muted-foreground">Aging/EOL Assets</p>
                </CardContent>
              </Card>

              <Card className="text-center p-4 border-green-200 dark:border-green-800">
                <CardContent className="space-y-2 pt-2">
                  <DollarSign className="h-8 w-8 mx-auto text-green-600" />
                  <div className="text-3xl font-bold text-green-600">
                    ${(assetLifecycleMetrics.totalReplacementCost / 1000).toFixed(0)}K
                  </div>
                  <p className="text-sm text-muted-foreground">Est. Replacement Cost</p>
                </CardContent>
              </Card>
            </div>

            {/* Assets Needing Attention Table */}
            {assetLifecycleMetrics.needingAttention.length > 0 ? (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <AlertTriangle className="h-5 w-5 text-orange-500" />
                    Assets Requiring Attention ({assetLifecycleMetrics.needingAttention.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 px-3 font-medium">Asset</th>
                          <th className="text-left py-2 px-3 font-medium">Type</th>
                          <th className="text-left py-2 px-3 font-medium">Age</th>
                          <th className="text-left py-2 px-3 font-medium">Warranty Status</th>
                          <th className="text-left py-2 px-3 font-medium">Lifecycle</th>
                          <th className="text-right py-2 px-3 font-medium">Replacement Cost</th>
                          <th className="text-center py-2 px-3 font-medium">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {assetLifecycleMetrics.needingAttention.slice(0, 10).map((asset: any) => {
                          const now = new Date();
                          const purchaseDate = asset.purchaseDate ? new Date(asset.purchaseDate) : null;
                          const ageYears = purchaseDate 
                            ? Math.floor((now.getTime() - purchaseDate.getTime()) / (1000 * 60 * 60 * 24 * 365))
                            : null;
                          
                          const warrantyEnd = asset.warrantyEndDate ? new Date(asset.warrantyEndDate) : null;
                          const warrantyExpired = warrantyEnd && warrantyEnd <= now;
                          const warrantyExpiringSoon = warrantyEnd && warrantyEnd > now && 
                            warrantyEnd <= new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000);

                          return (
                            <tr key={asset.id} className="border-b hover:bg-muted/50">
                              <td className="py-2 px-3">
                                <div className="font-medium">{asset.name}</div>
                                {asset.serialNumber && (
                                  <div className="text-xs text-muted-foreground">S/N: {asset.serialNumber}</div>
                                )}
                              </td>
                              <td className="py-2 px-3 capitalize">{asset.assetType?.replace('_', ' ')}</td>
                              <td className="py-2 px-3">
                                {ageYears !== null ? (
                                  <span className={ageYears >= 5 ? 'text-red-600 font-medium' : ageYears >= 3 ? 'text-orange-600' : ''}>
                                    {ageYears} year{ageYears !== 1 ? 's' : ''}
                                  </span>
                                ) : '-'}
                              </td>
                              <td className="py-2 px-3">
                                {warrantyExpired ? (
                                  <Badge variant="destructive" className="text-xs">Expired</Badge>
                                ) : warrantyExpiringSoon ? (
                                  <Badge variant="default" className="text-xs bg-orange-500">Expiring Soon</Badge>
                                ) : warrantyEnd ? (
                                  <Badge variant="secondary" className="text-xs">Active</Badge>
                                ) : (
                                  <span className="text-muted-foreground">-</span>
                                )}
                              </td>
                              <td className="py-2 px-3">
                                <Badge 
                                  variant={
                                    asset.lifecycleStage === 'end_of_life' ? 'destructive' :
                                    asset.lifecycleStage === 'aging' ? 'default' :
                                    'secondary'
                                  }
                                  className="text-xs capitalize"
                                >
                                  {asset.lifecycleStage?.replace(/_/g, ' ') || 'Unknown'}
                                </Badge>
                              </td>
                              <td className="py-2 px-3 text-right font-medium">
                                {(() => {
                                  const qbrCost = (currentQbr.assetReplacementCosts || {})[asset.id];
                                  const displayCost = qbrCost || asset.replacementCost;
                                  return displayCost 
                                    ? `$${parseFloat(displayCost).toLocaleString()}`
                                    : '-';
                                })()}
                              </td>
                              <td className="py-2 px-3 text-center">
                                {assetReplacements.some(r => r.assetId === asset.id) ? (
                                  <Badge variant="secondary" className="text-xs">
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    Added
                                  </Badge>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-xs h-7"
                                    onClick={() => {
                                      const qbrCost = (currentQbr.assetReplacementCosts || {})[asset.id];
                                      const replacementCost = qbrCost ? parseFloat(qbrCost) : (parseFloat(asset.replacementCost) || 5000);
                                      const newRec = {
                                        id: `asset-${asset.id}-${Date.now()}`,
                                        title: `Replace ${asset.name}`,
                                        description: `${asset.assetType?.replace('_', ' ')} replacement due to ${
                                          asset.lifecycleStage === 'end_of_life' ? 'end of life status' :
                                          asset.lifecycleStage === 'aging' ? 'aging hardware' :
                                          warrantyExpired ? 'expired warranty' : 'warranty expiring soon'
                                        }. Serial: ${asset.serialNumber || 'N/A'}`,
                                        priority: asset.lifecycleStage === 'end_of_life' || warrantyExpired ? 'high' : 'medium',
                                        timeline: asset.lifecycleStage === 'end_of_life' ? 'Immediate' : '30-90 days',
                                        budgetAllocated: replacementCost,
                                        targetQuarter: null,
                                        status: 'approved',
                                        category: 'Hardware Replacement',
                                        customerId: customer?.id,
                                        qbrId: currentQbr.id,
                                        assetId: asset.id,
                                        isAssetReplacement: true,
                                      };
                                      setAssetReplacements(prev => [...prev, newRec]);
                                      toast({
                                        title: "Replacement Added",
                                        description: `${asset.name} replacement added. Go to Budget Planning slide to assign to a quarter.`,
                                      });
                                    }}
                                  >
                                    <Plus className="h-3 w-3 mr-1" />
                                    Add to Budget
                                  </Button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {assetLifecycleMetrics.needingAttention.length > 10 && (
                    <p className="text-sm text-muted-foreground mt-3 text-center">
                      Showing 10 of {assetLifecycleMetrics.needingAttention.length} assets. View all in Asset Management.
                    </p>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card className="p-8 text-center">
                <CardContent className="space-y-3">
                  <CheckCircle className="h-12 w-12 mx-auto text-green-500" />
                  <h3 className="text-xl font-semibold text-foreground">All Assets in Good Standing</h3>
                  <p className="text-muted-foreground">
                    No assets currently require immediate attention for warranty or lifecycle concerns.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        );

      case "implementation_tracking": {
        // Implementation tracking status config
        const IMPL_STATUS_CONFIG: Record<string, { label: string; color: string; icon: any; bgColor: string }> = {
          approved: { label: "Not Started", color: "text-slate-500 dark:text-slate-200", icon: Clock, bgColor: "bg-gray-100 dark:bg-gray-800" },
          in_progress: { label: "In Progress", color: "text-blue-600 dark:text-blue-400", icon: PlayCircle, bgColor: "bg-blue-100 dark:bg-blue-900/30" },
          completed: { label: "Completed", color: "text-green-600 dark:text-green-400", icon: CheckCircle2, bgColor: "bg-green-100 dark:bg-green-900/30" },
          deferred: { label: "Deferred", color: "text-yellow-600 dark:text-yellow-400", icon: PauseCircle, bgColor: "bg-yellow-100 dark:bg-yellow-900/30" },
        };
        const IMPL_COLUMNS: string[] = ["approved", "in_progress", "completed", "deferred"];

        // Get all approved recommendations from ALL QBRs for this customer
        const implRecommendations: any[] = [];
        customerQbrs.forEach((q: any) => {
          if (!q.recommendations || !Array.isArray(q.recommendations)) return;
          const custApprovals = (q.customerApprovals as any[]) || [];
          q.recommendations.forEach((rec: any, idx: number) => {
            const approval = custApprovals[idx];
            const customerDecision = approval?.status || rec.status || "pending";
            if (
              customerDecision === "approved" ||
              rec.implementationStatus === "in_progress" ||
              rec.implementationStatus === "completed" ||
              rec.implementationStatus === "deferred"
            ) {
              let implStatus = "approved";
              if (rec.implementationStatus === "in_progress") implStatus = "in_progress";
              else if (rec.implementationStatus === "completed") implStatus = "completed";
              else if (rec.implementationStatus === "deferred") implStatus = "deferred";
              implRecommendations.push({
                id: `${q.id}-${idx}`,
                qbrId: q.id,
                index: idx,
                title: rec.title || "Untitled Recommendation",
                description: rec.description || "",
                category: rec.category || "general",
                priority: rec.priority || "medium",
                estimatedCost: rec.estimatedCost || null,
                budgetAllocated: rec.budgetAllocated || null,
                assignedTo: rec.assignedTo || null,
                completedAt: rec.completedAt || null,
                status: implStatus,
                qbrTitle: q.title || "Business Review",
              });
            }
          });
        });

        const implGrouped = IMPL_COLUMNS.reduce((acc, status) => {
          acc[status] = implRecommendations.filter((r) => r.status === status);
          return acc;
        }, {} as Record<string, any[]>);

        const implStats = {
          total: implRecommendations.length,
          notStarted: implGrouped.approved.length,
          inProgress: implGrouped.in_progress.length,
          completed: implGrouped.completed.length,
          deferred: implGrouped.deferred.length,
        };

        const handleImplDragStart = (e: React.DragEvent, rec: any) => {
          setImplDraggedItem(rec);
          e.dataTransfer.effectAllowed = "move";
        };

        const handleImplDragOver = (e: React.DragEvent) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = "move";
        };

        const handleImplDrop = (e: React.DragEvent, newStatus: string) => {
          e.preventDefault();
          if (!implDraggedItem || implDraggedItem.status === newStatus) {
            setImplDraggedItem(null);
            return;
          }
          const targetQbr = allQbrs.find((q: any) => q.id === implDraggedItem.qbrId);
          if (!targetQbr) { setImplDraggedItem(null); return; }

          const updatedRecs = (targetQbr.recommendations || []).map((r: any, idx: number) => {
            if (idx === implDraggedItem.index) {
              const updated = { ...r, implementationStatus: newStatus };
              if (newStatus === "completed") updated.completedAt = new Date().toISOString();
              else if (r.completedAt && newStatus !== "completed") updated.completedAt = null;
              return updated;
            }
            return r;
          });

          secureApiRequest(`/api/qbrs/${implDraggedItem.qbrId}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ recommendations: updatedRecs }),
          })
            .then(() => {
              queryClient.invalidateQueries({ queryKey: ["/api/qbrs"] });
              queryClient.invalidateQueries({ queryKey: ["/api/qbrs", implDraggedItem.qbrId] });
            })
            .catch(() => {
              toast({ title: "Error", description: "Failed to update status", variant: "destructive" });
            });
          setImplDraggedItem(null);
        };

        const formatImplCurrency = (value: string | null) => {
          if (!value) return null;
          const num = parseFloat(value);
          if (isNaN(num)) return null;
          return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(num);
        };

        return (
          <div className="space-y-4 h-full flex flex-col">
            <div className="text-center space-y-2">
              <h2 className="text-3xl font-bold text-foreground flex items-center justify-center gap-2">
                <Kanban className="h-7 w-7" />
                Implementation Tracking
              </h2>
              <p className="text-lg text-muted-foreground">
                Drag recommendations between columns to update implementation status
              </p>
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-5 gap-3">
              <Card><CardContent className="p-3 flex items-center gap-2"><Target className="h-4 w-4 text-muted-foreground" /><div><p className="text-xs text-muted-foreground">Total</p><p className="text-lg font-bold">{implStats.total}</p></div></CardContent></Card>
              <Card><CardContent className="p-3 flex items-center gap-2"><Clock className="h-4 w-4 text-gray-500" /><div><p className="text-xs text-muted-foreground">Not Started</p><p className="text-lg font-bold text-gray-600">{implStats.notStarted}</p></div></CardContent></Card>
              <Card><CardContent className="p-3 flex items-center gap-2"><PlayCircle className="h-4 w-4 text-blue-500" /><div><p className="text-xs text-muted-foreground">In Progress</p><p className="text-lg font-bold text-blue-600">{implStats.inProgress}</p></div></CardContent></Card>
              <Card><CardContent className="p-3 flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-500" /><div><p className="text-xs text-muted-foreground">Completed</p><p className="text-lg font-bold text-green-600">{implStats.completed}</p></div></CardContent></Card>
              <Card><CardContent className="p-3 flex items-center gap-2"><PauseCircle className="h-4 w-4 text-yellow-500" /><div><p className="text-xs text-muted-foreground">Deferred</p><p className="text-lg font-bold text-yellow-600">{implStats.deferred}</p></div></CardContent></Card>
            </div>

            {/* Kanban Board */}
            {implRecommendations.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Kanban className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-30" />
                  <h3 className="text-lg font-medium mb-2">No recommendations to track</h3>
                  <p className="text-muted-foreground text-sm">
                    Customer-approved recommendations will appear here after approval during the review.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-4 gap-3 flex-1 min-h-0">
                {IMPL_COLUMNS.map((status) => {
                  const config = IMPL_STATUS_CONFIG[status];
                  const StatusIcon = config.icon;
                  const items = implGrouped[status] || [];
                  return (
                    <div
                      key={status}
                      className="flex flex-col min-h-0"
                      onDragOver={handleImplDragOver}
                      onDrop={(e) => handleImplDrop(e, status)}
                    >
                      <div className={`rounded-t-lg p-2.5 ${config.bgColor}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <StatusIcon className={`h-4 w-4 ${config.color}`} />
                            <span className={`font-medium text-sm ${config.color}`}>{config.label}</span>
                          </div>
                          <Badge variant="secondary" className="text-xs">{items.length}</Badge>
                        </div>
                      </div>
                      <ScrollArea className="flex-1 min-h-[200px] max-h-[350px] border border-t-0 rounded-b-lg bg-muted/20 p-2">
                        <div className="space-y-2">
                          {items.map((rec: any) => (
                            <Card
                              key={rec.id}
                              className="cursor-grab hover:shadow-md transition-shadow"
                              draggable
                              onDragStart={(e) => handleImplDragStart(e, rec)}
                            >
                              <CardContent className="p-2.5">
                                <div className="flex items-start gap-1.5 mb-1.5">
                                  <GripVertical className="h-3.5 w-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
                                  <h4 className="font-medium text-xs line-clamp-2">{rec.title}</h4>
                                </div>
                                <div className="space-y-1 text-[10px]">
                                  <div className="flex items-center justify-between">
                                    <Badge
                                      variant="outline"
                                      className={`text-[9px] capitalize ${
                                        rec.priority === "critical" ? "border-red-500 text-red-600" :
                                        rec.priority === "high" ? "border-orange-500 text-orange-600" :
                                        rec.priority === "medium" ? "border-blue-500 text-blue-600" :
                                        "border-gray-400 text-gray-600"
                                      }`}
                                    >
                                      {rec.priority}
                                    </Badge>
                                    <Badge variant="secondary" className="text-[9px] capitalize">{rec.category}</Badge>
                                  </div>
                                  {(rec.budgetAllocated || rec.estimatedCost) && (
                                    <div className="flex items-center gap-1 text-muted-foreground">
                                      <DollarSign className="h-3 w-3" />
                                      <span>{formatImplCurrency(rec.budgetAllocated || rec.estimatedCost)}</span>
                                    </div>
                                  )}
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                          {items.length === 0 && (
                            <div className="text-center py-6 text-muted-foreground text-xs">
                              <p>No items</p>
                              <p className="mt-1">Drag recommendations here</p>
                            </div>
                          )}
                        </div>
                      </ScrollArea>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      }

      case "backup_status": {
        return (
          <div className="space-y-6">
            <div className="text-center space-y-2">
              <h2 className="text-3xl font-bold text-foreground">{slide.title}</h2>
              <p className="text-muted-foreground">{slide.subtitle}</p>
            </div>
            {slide.customerId && (
              <BackupStatusSlideContent customerId={slide.customerId} />
            )}
          </div>
        );
      }

      case "patch_status": {
        return (
          <div className="space-y-6">
            <div className="text-center space-y-2">
              <h2 className="text-3xl font-bold text-foreground">{slide.title}</h2>
              <p className="text-muted-foreground">{slide.subtitle}</p>
            </div>
            {slide.customerId && (
              <PatchStatusSlideContent customerId={slide.customerId} />
            )}
          </div>
        );
      }

      case "summary":
        return (
          <div className="space-y-8 max-w-4xl mx-auto">
            <div className="text-center space-y-2">
              <h2 className="text-4xl font-bold text-foreground">Schedule Next Business Review</h2>
              <p className="text-lg text-muted-foreground">
                Based on your {customer?.reviewFrequency || 'quarterly'} review cadence, schedule the next review with the customer
              </p>
            </div>

            <Card className="border-2">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Calendar className="h-5 w-5" />
                  <span>Next Review Date & Time</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {dateAdjustmentReason && (
                  <div className="flex items-center space-x-2 text-sm text-blue-700 bg-blue-50 dark:bg-blue-950/20 p-3 rounded-md border border-blue-200 dark:border-blue-800">
                    <Calendar className="h-4 w-4" />
                    <span>{dateAdjustmentReason}</span>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="nextReviewDate">Next Review Date</Label>
                    <Input
                      id="nextReviewDate"
                      type="date"
                      value={nextReviewDate}
                      onChange={(e) => setNextReviewDate(e.target.value)}
                      className="w-full"
                    />
                    <p className="text-xs text-muted-foreground">
                      Suggested date excludes weekends and US holidays
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="nextReviewTime">Preferred Time</Label>
                    <Input
                      id="nextReviewTime"
                      type="time"
                      value={nextReviewTime}
                      onChange={(e) => handleTimeChange(e.target.value)}
                      className="w-full"
                      min="09:00"
                      max="17:00"
                    />
                    <p className="text-xs text-muted-foreground">
                      Business hours: 9:00 AM - 5:00 PM
                    </p>
                  </div>
                </div>
                {timeWarning && (
                  <div className="flex items-center space-x-2 text-sm text-amber-700 bg-amber-50 dark:bg-amber-950/20 p-3 rounded-md border border-amber-200 dark:border-amber-800">
                    <Clock className="h-4 w-4" />
                    <span>{timeWarning}</span>
                  </div>
                )}
                {nextReviewDate && !timeWarning && (
                  <div className="flex items-center space-x-2 text-sm text-green-700 bg-green-50 dark:bg-green-950/20 p-3 rounded-md border border-green-200 dark:border-green-800">
                    <CheckCircle className="h-4 w-4" />
                    <span>
                      Next review scheduled for {formatLocalDateTime(
                        createLocalDateTime(nextReviewDate, nextReviewTime),
                        { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' }
                      )}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="flex justify-center space-x-3">
              <Button onClick={saveProgress} variant="outline" size="lg">
                <FileText className="h-4 w-4 mr-2" />
                Save Progress
              </Button>
              <Button
                onClick={() => finalizePresentationMutation.mutate()}
                className="bg-green-600 hover:bg-green-700"
                size="lg"
                disabled={finalizePresentationMutation.isPending || !nextReviewDate}
              >
                <Calendar className="h-4 w-4 mr-2" />
                Complete Presentation
              </Button>
            </div>
            {!nextReviewDate && (
              <p className="text-center text-sm text-amber-600">
                Please schedule the next review date before completing the presentation
              </p>
            )}
          </div>
        );

      default:
        return <div>Slide content</div>;
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-10xl h-[95vh] w-[95vw] flex flex-col focus:outline-none" tabIndex={-1}>
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center justify-between">
            <span>Business Review Presentation Mode</span>
            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowSlideConfig(true)}
                className="h-8"
              >
                <SlidersHorizontal className="h-4 w-4 mr-1" />
                Configure Slides
              </Button>
              <Badge variant="outline">
                Slide {currentSlide + 1} of {slides.length}
              </Badge>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4 min-h-0">{renderSlide()}</div>

        {/* Navigation Controls - Fixed at bottom */}
        <div className="flex-shrink-0 flex justify-between items-center border-t pt-4 bg-background">
          <Button
            variant="outline"
            onClick={() => navigateToSlide(Math.max(0, currentSlide - 1))}
            disabled={currentSlide === 0}
            size="sm"
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            Previous
          </Button>

          <div className="flex space-x-2">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => navigateToSlide(index)}
                className={`w-3 h-3 rounded-full transition-colors ${
                  index === currentSlide
                    ? "bg-blue-600"
                    : "bg-gray-300 dark:bg-gray-600"
                }`}
              />
            ))}
          </div>

          <Button
            variant="outline"
            onClick={() =>
              navigateToSlide(Math.min(slides.length - 1, currentSlide + 1))
            }
            disabled={currentSlide === slides.length - 1}
            size="sm"
          >
            Next
            <ChevronRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </DialogContent>

      {/* Budget Assignment Dialog */}
      <Dialog
        open={!!assigningRecommendation}
        onOpenChange={() => setAssigningRecommendation(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Recommendation to Quarter</DialogTitle>
          </DialogHeader>

          {assigningRecommendation && (
            <Form {...assignRecommendationForm}>
              <form
                onSubmit={assignRecommendationForm.handleSubmit(
                  onAssignRecommendation,
                )}
                className="space-y-3"
              >
                <div>
                  <h4 className="font-medium mb-2">
                    {assigningRecommendation.title}
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    {assigningRecommendation.description}
                  </p>
                </div>

                <FormField
                  control={assignRecommendationForm.control}
                  name="targetQuarter"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Target Quarter</FormLabel>
                      <FormControl>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select quarter" />
                          </SelectTrigger>
                          <SelectContent>
                            {getAllQuarters().map((quarter) => (
                              <SelectItem key={quarter} value={quarter}>
                                {quarter}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={assignRecommendationForm.control}
                  name="budgetAllocated"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Budget Allocation ($)</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="number"
                          placeholder="Enter budget amount"
                          min="0"
                          step="100"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={assignRecommendationForm.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status</FormLabel>
                      <FormControl>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="approved">Approved</SelectItem>
                            <SelectItem value="in_progress">
                              In Progress
                            </SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex justify-end space-x-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setAssigningRecommendation(null)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={updateRecommendationMutation.isPending}
                  >
                    {updateRecommendationMutation.isPending
                      ? "Saving..."
                      : "Save Assignment"}
                  </Button>
                </div>
              </form>
            </Form>
          )}
        </DialogContent>
      </Dialog>

      {/* Yearly Budget Edit Dialog */}
      <Dialog
        open={!!editingYearBudget}
        onOpenChange={() => setEditingYearBudget(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set Annual Budget for {editingYearBudget?.year}</DialogTitle>
          </DialogHeader>
          {editingYearBudget && (
            <div className="space-y-3">
              <div>
                <Label htmlFor="yearBudget">Annual Budget ($)</Label>
                <Input
                  id="yearBudget"
                  type="number"
                  min="0"
                  step="1000"
                  value={editingYearBudget.budget}
                  onChange={(e) =>
                    setEditingYearBudget({
                      ...editingYearBudget,
                      budget: parseFloat(e.target.value) || 0,
                    })
                  }
                  placeholder="Enter annual budget"
                />
              </div>
              <div className="flex justify-end space-x-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setEditingYearBudget(null)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    updateYearlyBudgetMutation.mutate({
                      year: editingYearBudget.year,
                      budget: editingYearBudget.budget,
                    });
                  }}
                  disabled={updateYearlyBudgetMutation.isPending}
                >
                  {updateYearlyBudgetMutation.isPending ? "Saving..." : "Save Budget"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Slide Configuration Dialog */}
      <Dialog open={showSlideConfig} onOpenChange={setShowSlideConfig}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <SlidersHorizontal className="h-5 w-5" />
              Configure Presentation Slides
            </DialogTitle>
          </DialogHeader>
          <SlideConfigPanel
            template={presentationTemplate}
            customerId={customer?.id}
            customerName={customer?.name}
            onSave={async (newConfig) => {
              // Save to this QBR's presentation config
              try {
                await secureApiRequest(`/api/qbrs/${qbr.id}`, {
                  method: "PATCH",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ presentationConfig: newConfig }),
                });
                queryClient.invalidateQueries({ queryKey: ["/api/qbrs", qbr.id] });
                queryClient.invalidateQueries({ queryKey: ["/api/qbrs"] });
                setLocalPresentationConfig(newConfig);
                setCurrentSlide(0); // Reset to first slide when config changes
                setShowSlideConfig(false);
                toast({
                  title: "Slides Updated",
                  description: "Presentation configuration saved for this review.",
                });
              } catch (error) {
                toast({
                  title: "Save Failed",
                  description: "Failed to save presentation configuration.",
                  variant: "destructive",
                });
              }
            }}
            onSaveForCustomer={async (newConfig) => {
              // Save to customer's presentation template
              try {
                const response = await secureApiRequest(`/api/customers/${customer.id}`, {
                  method: "PATCH",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ presentationTemplate: newConfig }),
                });
                if (!response.ok) {
                  throw new Error("Failed to save customer template");
                }
                queryClient.invalidateQueries({ queryKey: ["/api/customers", customer.id] });
                queryClient.invalidateQueries({ queryKey: ["/api/customers"] });
                setLocalPresentationConfig(newConfig);
                setCurrentSlide(0);
                setShowSlideConfig(false);
              } catch (error: any) {
                throw error;
              }
            }}
            onSaveAsDefault={async (newConfig) => {
              // Save to organization settings
              try {
                const response = await secureApiRequest("/api/organizations/settings", {
                  method: "PATCH",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ presentationTemplate: newConfig }),
                });
                if (!response.ok) {
                  throw new Error("Failed to save default template");
                }
                queryClient.invalidateQueries({ queryKey: ["/api/organizations/current"] });
                setLocalPresentationConfig(newConfig);
                setCurrentSlide(0);
                setShowSlideConfig(false);
              } catch (error: any) {
                throw error;
              }
            }}
            onClose={() => setShowSlideConfig(false)}
          />
        </DialogContent>
      </Dialog>
      
      {/* Next Review Creation Confirmation Dialog */}
      <AlertDialog open={showNextReviewConfirmation} onOpenChange={setShowNextReviewConfirmation}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Create Next Business Review?</AlertDialogTitle>
            <AlertDialogDescription>
              Would you like to schedule the next Business Review for {customer?.name}?
              {nextReviewDate && (
                <span className="block mt-2 font-medium">
                  Suggested date: {formatBusinessDate(new Date(nextReviewDate))}
                </span>
              )}
              <span className="block mt-2 text-muted-foreground">
                You can also create a Business Review manually later from the customer's page.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleSkipNextReview} disabled={isCreatingNextReview}>
              No, Skip for Now
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleCreateNextReview}
              disabled={isCreatingNextReview}
            >
              {isCreatingNextReview ? "Creating..." : "Yes, Create Next Review"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}